/* eslint-disable react/forbid-dom-props, react/forbid-component-props */
import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { 
  makeStyles, 
  tokens, 
  Button, 
  TabList, 
  Tab, 
  SelectTabEvent, 
  SelectTabData, 
  Divider, 
  Field, 
  Input, 
  Subtitle1, 
  Subtitle2, 
  Caption1, 
  Switch, 
  MessageBar, 
  MessageBarBody, 
  Card, 
  CardHeader, 
  CardFooter, 
  Body1,
  Spinner,
  Dropdown,
  Option,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Checkbox
} from "@fluentui/react-components";
import {
  Save24Regular,
  DocumentArrowRight24Regular,
  Attach24Regular,
  Delete24Regular,
  Edit24Regular,
  ArrowUpload24Regular,
  AppsListDetail24Regular,
  ClipboardPaste24Regular,
  TableStackLeft24Regular,
  LockClosed20Regular,
  DocumentDismiss24Regular,
  SlideText24Regular,
  Open24Regular,
} from "@fluentui/react-icons";
import Marketplace from "./Marketplace";
import { ExcelLiveNote, parseExcelContent } from "./ExcelLiveNote";
import { PowerPointLinkNote } from "./PowerPointLinkNote";
import ReactQuill, { Quill } from "react-quill";
import 'react-quill/dist/quill.snow.css';
import TemplateEditor from "./TemplateEditor";
import { templateToYaml, yamlToTemplate } from "../../services/templateYaml";
import { noteToMarkdown, markdownToNote } from "../../services/noteMarkdown";

// ─── Quill Link Sanitizer ──────────────────────────────────────────────────────
// Quill 1.x allows javascript: protocol links, which can redirect users to
// untrusted pages. This overrides the Link format to only allow safe protocols.
const QuillLink = Quill.import("formats/link") as any;
const SAFE_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];
class SafeLink extends QuillLink {
  static sanitize(url: string): string {
    try {
      const parsed = new URL(url);
      if (SAFE_LINK_PROTOCOLS.includes(parsed.protocol)) return url;
    } catch {
      // Relative URL or malformed — allow it to pass through to quill's default
      if (!url.startsWith("javascript:")) return url;
    }
    return "about:blank";
  }
}
Quill.register(SafeLink, true);
import { User } from 'firebase/auth';
import { 
  createNote, 
  getUserNotes,
  getLibraryNotes,
  getNote,
  updateNote,
  removeNote, 
  MarginNote,
  uploadAttachment,
  getTenantUsers,
  updateUserRole,
  checkEmailWhitelist,
  addToWhitelist,
  removeFromWhitelist,
  getTenantWhitelist,
  bulkAddToWhitelist
} from "../../services/db";
import { syncNoteToVault } from "../../services/sync";
import { onAuthStateChanged, logout, getGraphToken, getOneDriveProfile, loginWithSSO, getGraphTokenViaDialog, ensureUserProfile } from "../../services/auth";
import { OWNER_EMAILS } from "../../services/subscription";
import { getUserPlan, getTrialInfo, TrialInfo, checkNoteCap, checkAttachment, checkGlobalNote, clearPlanCache, Plan, MARKETPLACE_OFFER_URL } from "../../services/subscription";
import { resolveDriveItemFromUrl, getWorksheets } from "../../services/excel";
import { resolvePresentation, isSafeWebUrl, fetchCoverThumbnail } from "../../services/powerpoint";
import { 
  tagDocumentWithCreator, 
  getDocumentCreatorId, 
  getSubscriptionRecord,
  startPro7DayTrial,
  checkTemplateAccess
} from "../../services/saasService";
import { TEMPLATE_CATALOG } from "./Marketplace";
import {
  DisplayMode,
  DocumentDisplaySettings,
  ClassicIcon,
  CLASSIC_ICONS,
  DEFAULT_ICON_ID,
  DISPLAY_DEFAULTS,
  getDocumentDisplaySettings,
  saveDocumentDisplaySettings,
  getIconById,
  buildDiscreetHtml,
  buildAppendixLinkHtml,
} from "../../services/displayMode";
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage, db } from '../../config';

interface MarginMenuProps {
  user: User;
  hasSyncToken: boolean;
  setHasSyncToken: (val: boolean) => void;
  graphToken: string | null;
  setGraphToken: (val: string | null) => void;
  tenantId?: string;
  role?: string;
}

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ]
};

// Microsoft Marketplace listing for the transactable SaaS offer (margin-pro-saas).
// MARKETPLACE_OFFER_URL is imported from services/subscription (single source of truth).

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden"
  },
  attachmentZone: {
    height: "80px",
    border: `2px dashed #59766C`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground3,
    gap: "8px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    }
  },
  fileInput: {
    display: "none"
  },
  buttonGroup: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "8px"
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "16px",
    paddingBottom: "16px",
    width: "100%",
    boxSizing: "border-box"
  },
  signOutBtn: {
    color: "#59766C",
    ":hover": {
      color: "#3F564F",
      textDecorationLine: "underline"
    }
  },
  charCounter: {
    color: tokens.colorNeutralForeground3,
    marginTop: "4px"
  },
  editorWrapper: {
    minHeight: "245px",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    "& .ql-toolbar": {
      display: "flex",
      flexWrap: "wrap",
      backgroundColor: tokens.colorNeutralBackground3,
      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
      borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderTopRightRadius: tokens.borderRadiusMedium,
    },
    "& .ql-container": {
      fontSize: "14px",
      fontFamily: tokens.fontFamilyBase,
      height: "200px",
      minHeight: "200px",
      maxHeight: "200px",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
      borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      borderBottomRightRadius: tokens.borderRadiusMedium,
    },
    "& .ql-editor": {
      flex: 1,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: "12px"
    }
  },
  headerSticky: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: "6px 8px 0px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    boxSizing: "border-box",
    overflowX: "hidden"
  },
  scrollBody: {
    flexGrow: 1,
    minHeight: 0, // allow the flex child to shrink so overflowY actually scrolls
    overflowY: "auto",
    padding: "12px",
    boxSizing: "border-box"
  },
  noteCard: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    userSelect: "none",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: tokens.shadow4
    }
  },
  badge: {
    marginLeft: "8px",
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    padding: "2px 6px",
    borderRadius: tokens.borderRadiusMedium,
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase"
  }
});

const getOrCreateDocId = async (): Promise<string> => {
  try {
    if (typeof Office === "undefined" || !Office?.context?.document?.settings) return "doc_browser_fallback";
    
    let id = Office.context.document.settings.get("marginDocId");
    if (!id) {
      id = "doc_" + Date.now() + "_" + Math.random().toString(36).substring(2, 11);
      Office.context.document.settings.set("marginDocId", id);
      // Removed immediate saveAsync from sync path to prevent hangs
    }
    return id as string;
  } catch (error) {
    console.error("Doc ID identification error:", error);
    return "doc_browser_fallback";
  }
};

const getWordDocName = (): Promise<string> => {
  return new Promise((resolve) => {
    if (typeof Office === "undefined" || !Office.context?.document) {
      resolve("Unsaved Word Document");
      return;
    }
    Office.context.document.getFilePropertiesAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded && result.value.url) {
        const url = result.value.url;
        const name = url.split(/[\\/]/).pop();
        resolve(name || "Unsaved Word Document");
      } else {
        resolve("Unsaved Word Document");
      }
    });
  });
};

const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Secure, clean RFC4122 compliant UUID v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const MarginMenu: React.FC<MarginMenuProps> = ({ user, hasSyncToken, setHasSyncToken, graphToken, setGraphToken, tenantId, role }) => {
  if (!user) return <Spinner label="Loading user profile..." style={{ marginTop: '40px' }} />;
  const menuStyles = useStyles();

  const version = process.env.VITE_VERSION || "v1.0.3";
  const environment = process.env.VITE_ENVIRONMENT || "production";

  // Owner/developer conveniences (plan bypass, ownership bypass, cross-environment
  // tooling) are gated to non-production so they never ship to real users. Single
  // source of truth for every owner-only branch below.
  const isOwnerDev =
    environment !== 'production' &&
    (['johncyork@gmail.com', 'john@marginproapp.com'].includes((user.email || '').toLowerCase()) ||
      user.uid === 'yDaZjNiTedcayntpxTvl6whrp9o2');
  const [betaFeaturesEnabled, setBetaFeaturesEnabled] = useState(false);

  const [selectedValue, setSelectedValue] = useState<"new" | "library" | "management" | "marketplace" | "settings">("new");
  const [ownedTemplates, setOwnedTemplates] = useState<string[]>([]);
  /** Stores the Word-quality HTML for the currently loaded template, used during insertWordNote */
  const [templateWordHtml, setTemplateWordHtml] = useState<string | null>(null);
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
  const [lockedNotes, setLockedNotes] = useState<Record<string, boolean>>({});
  const [label, setLabel] = useState("");
  const [content, setContent] = useState("");
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error" | "warning" | "info", text: string } | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isGlobal, setIsGlobal] = useState(false);
  const [visibility, setVisibility] = useState<'private' | 'tenant_read' | 'tenant_edit'>('private');

  const [attachmentUrl, setAttachmentUrl] = useState<string>("");
  const [attachmentName, setAttachmentName] = useState<string>("");
  const [attachmentType, setAttachmentType] = useState<string>("");
  const [attachmentPath, setAttachmentPath] = useState<string>("");
  const [excelShareUrl, setExcelShareUrl] = useState<string>("");
  // Appendix rendering mode for Excel/PowerPoint notes (see MarginNote.appendixMode).
  const [appendixMode, setAppendixMode] = useState<'snapshot' | 'link' | 'embed'>('snapshot');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Excel Linking State
  const [excelInputMode, setExcelInputMode] = useState(false);
  const [excelUrlInput, setExcelUrlInput] = useState("");
  const [excelDriveId, setExcelDriveId] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelWorksheets, setExcelWorksheets] = useState<{id: string, name: string}[]>([]);
  const [excelSheetSelection, setExcelSheetSelection] = useState("");
  const [excelRangeSelection, setExcelRangeSelection] = useState("");
  const [pendingExcelUrl, setPendingExcelUrl] = useState<string | null>(null);
  // ── PowerPoint link-in (Part 1: cover thumbnail + open in PowerPoint) ──
  const [pptInputMode, setPptInputMode] = useState(false);
  const [pptUrlInput, setPptUrlInput] = useState("");
  const [pptLoading, setPptLoading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateYamlInputRef = useRef<HTMLInputElement>(null);
  const noteMdInputRef = useRef<HTMLInputElement>(null);

  const isInsertingRef = useRef(false);
  const notesRef = useRef<MarginNote[]>([]);
  const prevGraphTokenRef = useRef<string | null>(graphToken);

  const [notes, setNotes] = useState<MarginNote[]>([]);
  notesRef.current = notes;
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Second-stage prompt: shown only when the note being deleted is in the document,
  // letting the user choose to remove the in-doc marker too or keep the text.
  const [docRemovePromptId, setDocRemovePromptId] = useState<string | null>(null);
  const [libraryTab, setLibraryTab] = useState<"local" | "global">("local");
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingSyncNote, setPendingSyncNote] = useState<MarginNote | null>(null);
  const [vaultWebUrl, setVaultWebUrl] = useState<string | null>(null);

  // ─── Display Mode ───────────────────────────────────────────────────────────

  const [docDisplaySettings, setDocDisplaySettings] = useState<DocumentDisplaySettings>({ ...DISPLAY_DEFAULTS, overrides: {} });
  const [applyingDisplayMode, setApplyingDisplayMode] = useState(false);
  const [includeOverridesInApply, setIncludeOverridesInApply] = useState(false);
  const [displayModeNoteCount, setDisplayModeNoteCount] = useState(0);
  // Per-note override state (active while a note is open in the editor)
  const [noteDisplayOverride, setNoteDisplayOverride] = useState<'inherit' | 'visible' | 'discreet'>('inherit');
  const [noteIconOverride, setNoteIconOverride] = useState<string>('');

  const [curDocId, setCurDocId] = useState<string>("");
  const [curDocName, setCurDocName] = useState<string>("Loading Document...");
  const [isAppReady, setIsAppReady] = useState(false);
  const [showAllEnvironments, setShowAllEnvironments] = useState(false);
  const [hasAppendix, setHasAppendix] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const hasAppendixRef = useRef(false);
  hasAppendixRef.current = hasAppendix;
  // True while a note modal is open, and for a short cooldown after it closes.
  // Suppresses the selection-change events that fire when focus returns to the
  // document on close (which would otherwise re-open the modal). Set at open time
  // so it is already active no matter which close event fires first.
  const dialogActiveRef = useRef(false);
  // After a note's modal closes, Word fires one stray selection-change that resolves
  // back to the SAME note (focus return / caret restore), at an unpredictable delay
  // (observed 0.3s–2s later) — which would re-open the modal. We arm this with the
  // closed note's id and consume the next selection event that resolves to it, with
  // no dependence on timing. Genuine clicks on any other note (or a re-click after the
  // stray event is consumed) pass through normally.
  const suppressReopenRef = useRef<string | null>(null);
  // The currently-open note modal (Office.Dialog) and the note it shows, so opening a new
  // note can close the existing modal first — this lets a new selection switch the open
  // modal, and makes Office's "a dialog is already open" block (orphaned modal) impossible.
  const activeDialogRef = useRef<any>(null);
  const activeDialogNoteRef = useRef<string | null>(null);
  const closeActiveNoteDialog = () => {
    try { activeDialogRef.current?.close(); } catch (e) { /* already closed */ }
    activeDialogRef.current = null;
    activeDialogNoteRef.current = null;
    dialogActiveRef.current = false;
  };
  const [hadAppendix, setHadAppendix] = useState(false);

  // Subscription / plan state
  const [userPlan, setUserPlan] = useState<Plan>('Free');
  const [upgradePrompt, setUpgradePrompt] = useState<string | null>(null);
  const [isPaywalled, setIsPaywalled] = useState<boolean>(false);
  const [checkingLicense, setCheckingLicense] = useState<boolean>(false);

  // Guest Collaboration Gating
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [documentOwnerId, setDocumentOwnerId] = useState<string | null>(null);
  const [checkingGuestMode, setCheckingGuestMode] = useState(true);

  // Migration State
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [selectedMigrateIds, setSelectedMigrateIds] = useState<string[]>([]);

  const handleMigrateWorkspaces = async () => {
    if (selectedMigrateIds.length === 0) return;
    setIsMigrating(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('https://us-central1-margin-app-6fe10.cloudfunctions.net/migrateTenantData', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ tenantId: tenantId || null, documentIds: selectedMigrateIds })
      });
      if (res.ok) {
        const data = await res.json();
        setStatusMsg({ type: "success", text: `Successfully promoted ${data.count} notes to Production. Switch to the Production add-in to view them.` });
        // Remove migrated notes from the local UI
        setNotes(prev => prev.filter(n => !selectedMigrateIds.includes(n.documentId)));
        setShowMigrateDialog(false);
        setSelectedMigrateIds([]);
      } else {
        const errorText = await res.text();
        setStatusMsg({ type: "error", text: `Migration failed (${res.status}): ${errorText}` });
      }
    } catch (err: any) {
      console.error(err);
      setStatusMsg({ type: "error", text: `Migration error: ${err.message}` });
    } finally {
      setIsMigrating(false);
    }
  };

  // Enterprise Governance State
  const [isWhitelisted, setIsWhitelisted] = useState(true);
  const [checkingWhitelist, setCheckingWhitelist] = useState(true);
  const [adminUserList, setAdminUserList] = useState<any[]>([]);
  const [adminWhitelist, setAdminWhitelist] = useState<string[]>([]);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState("");
  const [importingCSV, setImportingCSV] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Stability Patch: Lazy initialization of document environment
  useEffect(() => {
    const initDoc = async () => {
      console.log("MarginMenu: Initializing document data...");
      const id = await getOrCreateDocId();
      const name = await getWordDocName();
      setCurDocId(id);
      setCurDocName(name);
      setIsAppReady(true);
      
      // Safe Background Save: 
      if (Office?.context?.document?.settings) {
        Office.context.document.settings.saveAsync();
      }
    };
    initDoc();
  }, []);

  // Load subscription plan once user is available
  useEffect(() => {
    if (user?.uid) {
      if (isOwnerDev) {
        setUserPlan('Professional');
      } else {
        getUserPlan(user.uid).then(setUserPlan).catch(() => setUserPlan('Free'));
        getTrialInfo(user.uid).then(setTrialInfo).catch(() => {});
      }
    }
    return () => clearPlanCache(); // Clear cache on unmount / sign out
  }, [user?.uid, user?.email]);

  // Verify Active AppSource Transactable License via Microsoft Graph API
  const verifyLicenseStatus = async () => {
    if (!user?.uid) return;
    setCheckingLicense(true);
    try {
      // 1. Bypass for staging environment and owner accounts.
      // user.email is null for custom-token auth — check displayName too
      // (set to the user's real email via updateProfile during loginWithSSO).
      const identityEmail = (user.email || user.displayName || '').toLowerCase();
      // Bypass for owner accounts, staging, and Microsoft certification reviewers.
      // Microsoft reviewers authenticate with their own @microsoft.com accounts.
      const isMicrosoftReviewer = identityEmail.endsWith('@microsoft.com');
      if (
        process.env.VITE_ENVIRONMENT === 'staging' ||
        OWNER_EMAILS.has(identityEmail) ||
        isMicrosoftReviewer
      ) {
        setIsPaywalled(false);
        return;
      }

      // 1b. Entitlement via getUserPlan — the single source of truth for access.
      // This covers the universal 30-day trial (computed from the account's createdAt)
      // as well as paid Personal/Professional/Enterprise plans. Anything other than
      // 'Free' is licensed and must NOT see the paywall. This is what lets every
      // reviewer/user reach full functionality on first sign-in regardless of account
      // type (personal MSA, *.onmicrosoft.com, blank-email, vendor domains, etc.).
      const effectivePlan = await getUserPlan(user.uid);
      if (effectivePlan !== 'Free') {
        setIsPaywalled(false);
        return;
      }

      // 2. Read Subscription Status from database.
      // Professional, Enterprise, and Trial plans are fully licensed —
      // skip the Graph SKU check (plan is set server-side by Cloud Functions
      // and is protected from client writes by Firestore rules).
      const sub = await getSubscriptionRecord(user.uid);
      const planLower = sub.plan?.toLowerCase();
      if (planLower === 'professional' || planLower === 'enterprise' || sub.status === 'trial') {
        setIsPaywalled(false);
        return;
      }

      // 3. Self-managed (ISV) licensing — entitlement is determined entirely by our
      // own records above (getUserPlan + subscription record), which are provisioned
      // by the SaaS Fulfillment API (resolveSubscription / billing webhook). We do NOT
      // use the Graph usageRights/licenseDetails API. A user reaching this point is
      // genuinely unlicensed (Free, no active subscription, trial elapsed) → paywall.
      setIsPaywalled(true);
    } catch (err) {
      console.error("License check error:", err);
      setIsPaywalled(true);
    } finally {
      setCheckingLicense(false);
    }
  };

  // ─── Stripe billing (personal Microsoft accounts) ────────────────────────────
  // Work/school accounts buy through the Microsoft Marketplace; personal MSA accounts
  // (tenant 'common') can't transact there, so they pay via Stripe. Routing by account
  // type is what keeps us compliant with AppSource policy.
  const isPersonalAccount = !tenantId || tenantId === 'common';
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [trialBarDismissed, setTrialBarDismissed] = useState(false);

  // Refresh the plan after checkout. The Stripe webhook that writes the plan can lag a
  // few seconds behind the dialog closing, so poll until the plan lands (or attempts
  // run out) instead of reading once and giving up on a stale 'Free'.
  const refreshPlan = (attempts = 6) => {
    clearPlanCache();
    getTrialInfo(user.uid).then(setTrialInfo).catch(() => {});
    getUserPlan(user.uid)
      .then((p) => {
        setUserPlan(p);
        verifyLicenseStatus();
        if (p === 'Free' && attempts > 1) {
          setTimeout(() => refreshPlan(attempts - 1), 2000);
        }
      })
      .catch(() => {
        if (attempts > 1) setTimeout(() => refreshPlan(attempts - 1), 2000);
      });
  };

  // Entry point for subscribing during the trial (or any upgrade): personal → Stripe
  // picker, work/school → marketplace listing.
  const startSubscribe = () => {
    if (isPersonalAccount) setShowPlanPicker(true);
    else window.open(MARKETPLACE_OFFER_URL, "_blank");
  };

  // Opens a Stripe-hosted URL inside an Office dialog via our own-domain bounce page
  // (keeps the dialog origin trusted → no manifest AppDomains change / re-cert).
  // Opens the Stripe dialog. The loading state (stripeBusy) and the plan picker are
  // cleared inside the displayDialogAsync callback — i.e. only once the dialog has
  // actually opened — so the "Opening secure checkout…" spinner stays visible across
  // the whole 3–4s gap (function call + dialog open), instead of flashing off early.
  const openStripeDialog = (stripeUrl: string) => {
    const goUrl = new URL("stripe-go.html", window.location.href);
    goUrl.searchParams.set("to", stripeUrl);
    Office.context.ui.displayDialogAsync(
      goUrl.toString(),
      { height: 75, width: 55, displayInIframe: false },
      (asyncResult) => {
        setStripeBusy(false);
        setShowPlanPicker(false);
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          setStatusMsg({ type: 'error', text: 'Could not open checkout. Please try again.' });
          return;
        }
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
          try {
            const data = JSON.parse(arg.message);
            if (data.type === "STRIPE_DONE" || data.type === "STRIPE_CANCEL") {
              dialog.close();
              if (data.type === "STRIPE_DONE") refreshPlan();
            }
          } catch (e) { /* ignore non-JSON */ }
        });
        // Fallback: refresh when the dialog is closed manually.
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => refreshPlan());
      }
    );
  };

  const openStripeCheckout = async (plan: 'personal' | 'professional', interval: 'month' | 'year') => {
    if (stripeBusy) return;
    setStripeBusy(true);
    try {
      const fns = getFunctions(db.app, 'us-central1');
      const call = httpsCallable(fns, 'createStripeCheckoutSession');
      // If still in the free trial, pass its end so Stripe doesn't charge until then
      // (subscribe early, keep your remaining free days).
      const trialEndUnix = trialInfo?.onTrial && trialInfo.trialEnd
        ? Math.floor(trialInfo.trialEnd.getTime() / 1000)
        : undefined;
      const res: any = await call({ plan, interval, returnOrigin: window.location.origin, trialEnd: trialEndUnix });
      const url = res?.data?.url;
      if (!url) throw new Error('No checkout URL returned.');
      openStripeDialog(url); // clears stripeBusy + closes the picker once the dialog opens
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err?.message || 'Could not start checkout. Please try again.' });
      setStripeBusy(false);
    }
  };

  const openStripePortal = async () => {
    if (stripeBusy) return;
    setStripeBusy(true);
    try {
      const fns = getFunctions(db.app, 'us-central1');
      const call = httpsCallable(fns, 'createStripePortalSession');
      const res: any = await call({ returnOrigin: window.location.origin });
      const url = res?.data?.url;
      if (!url) throw new Error('No portal URL returned.');
      openStripeDialog(url);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err?.message || 'Could not open the billing portal.' });
      setStripeBusy(false);
    }
  };

  // ── Template YAML export / import ──────────────────────────────────────────
  const exportTemplateYaml = () => {
    try {
      const html = content || templateWordHtml || "";
      const yamlText = templateToYaml(label, html);
      const safeName = (label || "margin-pro-template").replace(/[^a-z0-9._-]+/gi, "_").toLowerCase();
      const url = URL.createObjectURL(new Blob([yamlText], { type: "text/yaml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.yaml`;
      a.click();
      URL.revokeObjectURL(url);
      setStatusMsg({ type: "success", text: "Template exported as a .yaml file." });
    } catch (err: any) {
      setStatusMsg({ type: "error", text: err?.message || "Could not export this template." });
    }
  };

  const onTemplateYamlFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-importing the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { name, html } = yamlToTemplate(String(reader.result || ""));
        setLabel(name);
        setContent(html);
        setTemplateWordHtml(html);
        setCurrentTemplateId(null);
        setStatusMsg({ type: "success", text: `"${name}" template imported. Edit and save it as a note.` });
      } catch (err: any) {
        setStatusMsg({ type: "error", text: err?.message || "Could not read that template file." });
      }
    };
    reader.onerror = () => setStatusMsg({ type: "error", text: "Could not read that file." });
    reader.readAsText(file);
  };

  // Table content exports as YAML; free-form prose exports as Markdown.
  const editingIsTable = () => /<table[\s>]/i.test(content || templateWordHtml || "");

  // ── Free-form note Markdown export / import ────────────────────────────────
  const exportNoteMarkdown = () => {
    try {
      if (!content || !content.trim()) {
        setStatusMsg({ type: "warning", text: "There's nothing to export yet." });
        return;
      }
      const md = noteToMarkdown(label, content);
      const safeName = (label || "margin-pro-note").replace(/[^a-z0-9._-]+/gi, "_").toLowerCase();
      const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}.md`;
      a.click();
      URL.revokeObjectURL(url);
      setStatusMsg({ type: "success", text: "Note exported as a Markdown (.md) file." });
    } catch (err: any) {
      setStatusMsg({ type: "error", text: err?.message || "Could not export this note." });
    }
  };

  const onNoteMdFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-importing the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { name, html } = markdownToNote(String(reader.result || ""));
        // Free-form note → regular text editor (clear any table template mode).
        setTemplateWordHtml(null);
        setCurrentTemplateId(null);
        setLabel(name);
        setContent(html);
        setStatusMsg({ type: "success", text: `"${name}" note imported. Edit and save it.` });
      } catch (err: any) {
        setStatusMsg({ type: "error", text: err?.message || "Could not read that note file." });
      }
    };
    reader.onerror = () => setStatusMsg({ type: "error", text: "Could not read that file." });
    reader.readAsText(file);
  };

  // Reusable Stripe plan buttons (Personal / Professional × monthly / annual).
  const renderStripePlanOptions = () => {
    if (stripeBusy) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '24px 0', width: '100%' }}>
          <Spinner size="small" />
          <span style={{ fontSize: '13px', color: '#9fd3c4', fontWeight: 600 }}>Opening secure checkout…</span>
        </div>
      );
    }
    const opt = (label: string, sub: string, plan: 'personal' | 'professional', interval: 'month' | 'year') => (
      <button
        disabled={stripeBusy}
        onClick={() => openStripeCheckout(plan, interval)}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px',
          width: '100%', padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
          background: 'rgba(47,133,112,0.12)', border: '1px solid rgba(47,133,112,0.4)',
          color: '#fff', textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: '12px', color: '#9fd3c4' }}>{sub}</span>
      </button>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
        {opt('Personal — $5.99/mo', 'or $59.90/year (2 months free)', 'personal', 'month')}
        {opt('Personal — $59.90/yr', 'Best value · save ~17%', 'personal', 'year')}
        {opt('Professional — $9.99/mo', 'or $99.90/year (2 months free)', 'professional', 'month')}
        {opt('Professional — $99.90/yr', 'Best value · save ~17%', 'professional', 'year')}
        <p style={{ fontSize: '11px', color: '#9fd3c4', lineHeight: 1.4, marginTop: '2px' }}>
          Plans renew automatically at the listed price until you cancel. If a free trial applies, your first charge occurs when it ends. Cancel anytime via <strong>Manage</strong>. See our{' '}
          <a href="https://marginproapp.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#9fd3c4', textDecoration: 'underline' }}>Terms</a>.
        </p>
      </div>
    );
  };

  // Lightweight overlay used by inline "Upgrade" CTAs for personal accounts.
  const renderPlanPicker = () => (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(9,26,24,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }} onClick={() => setShowPlanPicker(false)}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#0f2d27', border: '1px solid rgba(47,133,112,0.4)', borderRadius: '14px',
        padding: '20px', width: '100%', maxWidth: '320px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <strong style={{ color: '#fff', fontSize: '15px' }}>Choose your plan</strong>
          <span onClick={() => setShowPlanPicker(false)} style={{ cursor: 'pointer', color: '#9fd3c4', fontSize: '18px' }}>×</span>
        </div>
        {renderStripePlanOptions()}
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '12px', marginBottom: 0 }}>
          Secure checkout by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );

  useEffect(() => {
    verifyLicenseStatus();
  }, [user?.uid, graphToken]);

  // ─── Collaboration Access Gate & XML Verification ───
  useEffect(() => {
    if (!isAppReady || !user?.uid) return;

    let active = true;

    const verifyOwnership = async () => {
      setCheckingGuestMode(true);
      try {
        let creatorId = await getDocumentCreatorId();
        if (!creatorId) {
          console.log("[CustomXML] No creator ID found on document load. Tagging document with current user:", user.uid);
          await tagDocumentWithCreator(user.uid);
          creatorId = user.uid;
        }

        if (!active) return;
        setDocumentOwnerId(creatorId);

        const isBypassUser = isOwnerDev;

        if (
          creatorId === user.uid ||
          isBypassUser ||
          userPlan !== 'Free'
        ) {
          setIsGuestMode(false);
        } else {
          console.log("[CustomXML] Viewing shared document as guest. Owner is:", creatorId);
          // Check subscription status
          const sub = await getSubscriptionRecord(user.uid);
          if (!active) return;

          const hasPaidPlan = 
            sub.plan === "Personal" || 
            sub.plan === "Professional" || 
            sub.plan === "Personal Pro" || 
            sub.plan === "enterprise" || 
            sub.status === "trial" || 
            sub.status === "active";

          if (hasPaidPlan) {
            setIsGuestMode(false);
          } else {
            // Collaborating non-subscribers are locked to guest mode
            setIsGuestMode(true);
          }
        }
      } catch (err) {
        console.error("Collaboration verification failed:", err);
      } finally {
        if (active) {
          setCheckingGuestMode(false);
        }
      }
    };

    verifyOwnership();

    return () => {
      active = false;
    };
  }, [isAppReady, user?.uid, userPlan]);

  const handleStartTrialFromGuestMode = async () => {
    setCheckingGuestMode(true);
    try {
      await startPro7DayTrial(user.uid);
      setIsGuestMode(false);
      setUserPlan('Personal');
      setStatusMsg({ type: "success", text: "Subscription successfully activated! Your 7-day Pro Free Trial is active. Access unlocked!" });
    } catch (err) {
      console.error("Trial activation failed from Guest Mode:", err);
      setStatusMsg({ type: "error", text: "Failed to active trial." });
    } finally {
      setCheckingGuestMode(false);
    }
  };

  const renderGuestModeUI = () => (
    <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: '#fcebe9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
        color: '#d9381e',
        border: '1.5px solid #f3a99f'
      }}>
        <LockClosed20Regular style={{ width: '32px', height: '32px' }} />
      </div>
      
      <Subtitle1 block style={{ fontWeight: 700, color: tokens.colorNeutralForeground1, marginBottom: '8px' }}>
        Guest Mode Active
      </Subtitle1>
      
      <Body1 block style={{ color: tokens.colorNeutralForeground3, fontSize: '13px', lineHeight: '1.5', marginBottom: '20px' }}>
        This document belongs to another user. Start your 7-Day Free Trial to edit contents, attach spreadsheets, and generate premium AI notes in real time.
      </Body1>

      <div style={{
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground2,
        border: `1.5px dashed ${tokens.colorNeutralStroke2}`,
        borderRadius: '12px',
        padding: '14px',
        textAlign: 'left',
        marginBottom: '24px',
        boxSizing: 'border-box'
      }}>
        <Subtitle2 block style={{ fontWeight: 700, color: '#2f8570', marginBottom: '8px' }}>🚀 Unlock Pro Features:</Subtitle2>
        <Caption1 block style={{ color: tokens.colorNeutralForeground2, fontSize: '12px', lineHeight: '1.6' }}>
          • Full collaborative editing &amp; note insertions<br />
          • Direct Excel live syncing &amp; file attachments<br />
          • 5 Free monthly template unlocks<br />
          • Cross-device document search &amp; indexing
        </Caption1>
      </div>

      <Button 
        appearance="primary" 
        size="large"
        style={{ backgroundColor: '#59766C', color: '#fff', width: '100%', fontWeight: 600, padding: '12px' }}
        onClick={handleStartTrialFromGuestMode}
      >
        Start 7-Day Free Trial
      </Button>
      
      <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: '8px' }}>
        No upfront commitment — cancel anytime.
      </Caption1>
    </div>
  );

  const renderPaywallUI = () => (
    <div style={{ 
      padding: '32px 20px', 
      textAlign: 'center', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      boxSizing: 'border-box',
      minHeight: '100vh',
      backgroundColor: '#091a18', // Sleek dark brand theme matching landing page
      color: '#fff'
    }}>
      <div style={{
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        backgroundColor: 'rgba(233, 76, 56, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px',
        color: '#e94c38',
        border: '1.5px solid rgba(233, 76, 56, 0.3)'
      }}>
        <LockClosed20Regular style={{ width: '36px', height: '36px' }} />
      </div>
      
      <Subtitle1 block style={{ fontWeight: 700, color: '#fff', marginBottom: '8px', fontSize: '18px' }}>
        License Required
      </Subtitle1>
      
      <Body1 block style={{ color: '#a0b0ad', fontSize: '13px', lineHeight: '1.6', marginBottom: '24px' }}>
        Your active Microsoft profile does not have an assigned seat for the Margin Pro add-in. Please subscribe or assign a pilot license to your Entra ID user account.
      </Body1>

      <div style={{
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'left',
        marginBottom: '28px',
        boxSizing: 'border-box'
      }}>
        <Subtitle2 block style={{ fontWeight: 700, color: '#7ec0ac', marginBottom: '8px' }}>Active SaaS subscription unlocks:</Subtitle2>
        <Caption1 block style={{ color: '#c5d5d2', fontSize: '12px', lineHeight: '1.7' }}>
          • 15+ Professional Templates (Strategic, PM, Academic)<br />
          • Infinite Note Records & Cloud-Sync Backups<br />
          • Deep Link live spreadsheets in Excel & Word<br />
          • Instant Bidirectional Appendix Generation
        </Caption1>
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {isPersonalAccount ? (
          // Personal Microsoft accounts can't transact in the marketplace → pay via Stripe.
          renderStripePlanOptions()
        ) : (
          // Work/school accounts purchase through the Microsoft Commercial Marketplace.
          <Button
            appearance="primary"
            size="large"
            style={{
              background: 'linear-gradient(135deg, #2f8570, #1a5548)',
              border: 'none',
              color: '#fff',
              width: '100%',
              fontWeight: 600,
              padding: '12px',
              fontSize: '14px',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
            onClick={() => window.open(MARKETPLACE_OFFER_URL, "_blank")}
          >
            Purchase on AppSource
          </Button>
        )}

        <Button
          appearance="outline" 
          size="medium"
          style={{ 
            color: '#7ec0ac', 
            borderColor: 'rgba(126, 192, 172, 0.3)', 
            width: '100%', 
            fontWeight: 500, 
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
          onClick={verifyLicenseStatus}
          disabled={checkingLicense}
        >
          {checkingLicense ? <Spinner size="tiny" label="Checking..." labelPosition="after" /> : "Re-verify License Status"}
        </Button>
      </div>
      
      <Caption1 style={{ color: '#7ec0ac', opacity: 0.5, marginTop: '16px', fontSize: '11px' }}>
        Have an organization code? Contact your pilot administrator.
      </Caption1>
    </div>
  );

  // Fetch Direct Vault URL for AI Discovery
  useEffect(() => {
    if (graphToken) {
      const fetchVaultUrl = async () => {
        try {
          const response = await fetch("https://graph.microsoft.com/v1.0/me/drive/root:/MarginPro_Vault", {
            headers: { "Authorization": `Bearer ${graphToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.webUrl) {
              console.log("MarginPro: Vault WebURL identified for AI Discovery.");
              setVaultWebUrl(data.webUrl);
            }
          }
        } catch (err) {
          console.error("Failed to fetch vault webUrl:", err);
        }
      };
      fetchVaultUrl();
    }
  }, [graphToken]);

  // Load document display settings from Custom XML when the document is ready
  useEffect(() => {
    if (!isAppReady) return;
    getDocumentDisplaySettings().then(settings => {
      setDocDisplaySettings(settings);
    });
  }, [isAppReady, curDocId]);

  const getIconForDocument = (iconId: string): ClassicIcon =>
    CLASSIC_ICONS.find(i => i.id === iconId) ?? CLASSIC_ICONS[0];

  // Keep the Apply Now note count in sync with notes in the current document
  useEffect(() => {
    const docNotes = notesRef.current.filter(n => n.documentId === curDocId && !n.isGlobal);
    setDisplayModeNoteCount(docNotes.length);
  }, [notes, curDocId]);

  // OneDrive Backfill — runs once when graphToken transitions null → a real token.
  // Syncs all notes the user already had before OneDrive was connected so the vault
  // is complete from day one, with no manual re-saving required.
  useEffect(() => {
    const wasNull = prevGraphTokenRef.current === null;
    prevGraphTokenRef.current = graphToken;

    if (!graphToken || !wasNull) return; // only on null → token transition
    const toSync = notesRef.current;
    if (toSync.length === 0) return;

    console.log(`MarginPro: OneDrive connected — backfilling ${toSync.length} existing notes to vault`);
    toSync.forEach(note => {
      syncNoteToVault(note, graphToken); // fire-and-forget, no UI blocker
    });
  }, [graphToken]);

  // Enterprise Governance: Check Whitelist
  useEffect(() => {
    if (user && tenantId && role !== 'admin') {
      setCheckingWhitelist(true);
      checkEmailWhitelist(tenantId, user.email || "").then(isOk => {
        setIsWhitelisted(isOk);
        setCheckingWhitelist(false);
      }).catch(err => {
        console.error("Governance Check Failed:", err);
        setCheckingWhitelist(false);
      });
    } else {
      setCheckingWhitelist(false);
    }
  }, [user, tenantId, role]);

  // Management Tab: Fetch Data
  useEffect(() => {
    if (selectedValue === "management" && tenantId && role === 'admin') {
      const fetchAdminData = async () => {
        const users = await getTenantUsers(tenantId);
        const whitelist = await getTenantWhitelist(tenantId);
        setAdminUserList(users);
        setAdminWhitelist(whitelist);
      };
      fetchAdminData();
    }
  }, [selectedValue, tenantId, role]);

  // Background monitor for document name changes and appendix detection
  useEffect(() => {
    if (!isAppReady) return () => {};

    const checkDocName = async () => {
      const newName = await getWordDocName();
      
      // Detect save/rename and trigger bulk auto-sync to update METADATA inside files
      if (newName !== curDocName && isAppReady) {
        console.log(`MarginPro: Document renamed to ${newName}. Refreshing vault metadata...`);
        setCurDocName(newName);
        
        const docNotes = notes.filter(n => n.documentId === curDocId && !n.isGlobal);
        // Silent background sync with updated documentName
        for (const note of docNotes) {
          syncNoteToVault({ ...note, documentName: newName }, graphToken);
        }
      }

      // Check if appendix content control exists in the document
      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load("items/tag");
        await context.sync();
        
        let found = false;
        for (const cc of contentControls.items) {
          if (cc.tag === "marginpro_appendix") {
            found = true;
            break;
          }
        }
        setHasAppendix(found);
      }).catch(() => {});
    };
    const interval = setInterval(checkDocName, 10000); // Check every 10s
    checkDocName(); // Initial check
    return () => clearInterval(interval);
  }, [curDocId, curDocName, notes, isAppReady]);

  useEffect(() => {
    // Move the caret to just after a note's content control. Called when the note
    // modal closes so a repeat click on the same note registers as a real selection
    // change (Word suppresses DocumentSelectionChanged when the caret doesn't move).
    // The stray selection event this (and Word's focus return) fires is handled by the
    // suppressReopenRef consume-once guard, not by timing.
    const moveCaretAfterNote = (noteId: string) => {
      Word.run(async (context) => {
        const ccs = context.document.contentControls;
        ccs.load("items/tag");
        await context.sync();
        const target = ccs.items.find(cc => cc.tag && cc.tag.split("|")[0] === noteId);
        if (!target) return;
        const after = target.getRange(Word.RangeLocation.after);
        after.select(Word.SelectionMode.start);
        await context.sync();
      }).catch(err => console.error("moveCaretAfterNote error:", err));
    };

    const selectionHandler = async (_eventArgs: any) => {
      // NOTE: a dialog being open no longer short-circuits here — we let the event resolve
      // the clicked note, then (below) either ignore a re-select of the same note or switch
      // the open modal to the newly-selected note.
      if (isInsertingRef.current || !isAppReady || hasAppendixRef.current) return;

      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const parentCc = selection.parentContentControlOrNullObject;
        parentCc.load("tag");
        // Pre-load all content controls in the SAME round-trip so the boundary
        // fallback below needs only one extra sync (for the location compare) rather
        // than a separate load — keeps label clicks fast.
        const allCcs = context.document.contentControls;
        allCcs.load("items/tag");
        await context.sync();

        // Resolve the note's content control. Clicking the leading edge of a content
        // control places the caret just BEFORE it, so parentContentControl comes back
        // null even though the user clicked the note — this is why, in discreet mode,
        // only the icon (at the END of the control) reliably opened the modal. When the
        // parent lookup misses, fall back to locating the note control whose range the
        // selection sits inside or immediately adjacent to, making the whole label
        // clickable like visible mode. This does not modify the user's selection.
        let ccOrNull: Word.ContentControl = parentCc;
        if (parentCc.isNullObject) {
          const noteCcs = allCcs.items.filter(c => c.tag && c.tag.includes("|"));
          const comparisons = noteCcs.map(c => ({ cc: c, rel: selection.compareLocationWith(c.getRange()) }));
          await context.sync();
          const hit = comparisons.find(({ rel }) =>
            rel.value === Word.LocationRelation.inside ||
            rel.value === Word.LocationRelation.insideStart ||
            rel.value === Word.LocationRelation.insideEnd ||
            rel.value === Word.LocationRelation.equal ||
            rel.value === Word.LocationRelation.adjacentBefore
          );
          if (hit) ccOrNull = hit.cc;
        }

        if (!ccOrNull.isNullObject && ccOrNull.tag) {
          if (ccOrNull.tag === "marginpro_appendix") {
            return;
          }

          // A valid MarginPro note tag always contains '|' (format: noteId|font|size|...).
          // Only load 'text' for the empty-placeholder cleanup when the tag looks unusual.
          const hasValidTag = ccOrNull.tag.includes("|");
          if (!hasValidTag) {
            // Unusual tag — load text to check for Word placeholder and clean up if needed
            ccOrNull.load("text");
            await context.sync();
            const currentText = ccOrNull.text ? ccOrNull.text.trim() : "";
            if (!currentText || currentText.includes("Click or tap")) {
              const parts = ccOrNull.tag.split("|");
              ccOrNull.insertText("", Word.InsertLocation.replace);
              ccOrNull.delete(true);
              await context.sync();
              if (parts.length > 1) {
                const [ , name, size, color, bold, italic, underline, highlight] = parts;
                const postDeleteSelection = context.document.getSelection();
                if (name && name !== "null" && name !== "undefined") postDeleteSelection.font.name = name;
                if (size && size !== "null" && size !== "undefined" && !isNaN(parseFloat(size))) postDeleteSelection.font.size = parseFloat(size);
                if (color && color !== "null" && color !== "undefined") postDeleteSelection.font.color = color;
                else postDeleteSelection.font.color = "";
                postDeleteSelection.font.bold = (bold === "true");
                postDeleteSelection.font.italic = (italic === "true");
                if (underline && underline !== "null" && underline !== "undefined" && underline !== "None") postDeleteSelection.font.underline = underline as Word.UnderlineType;
                else postDeleteSelection.font.underline = Word.UnderlineType.none;
                if (highlight && highlight !== "null" && highlight !== "undefined" && highlight !== "None") postDeleteSelection.font.highlightColor = highlight;
                else postDeleteSelection.font.highlightColor = "";
                await context.sync();
              }
              return;
            }
          }

          const noteId = ccOrNull.tag.split("|")[0];

          // Consume the post-close stray re-selection of the same note (see ref decl).
          if (suppressReopenRef.current === noteId) {
            suppressReopenRef.current = null;
            return;
          }
          // Any genuine open of a different note clears a pending suppression.
          suppressReopenRef.current = null;

          // Cache the note first so an in-place dialog switch (messageChild) can read it.
          const foundNote = notesRef.current.find(n => n.id === noteId);
          if (foundNote) {
            try {
              localStorage.setItem("marginpro_active_note_" + noteId, JSON.stringify(foundNote));
            } catch (e) {
              console.error("Failed to write selected note to localStorage:", e);
            }
          }

          // Claim the dialog slot synchronously (no await between check and set) so a second
          // selection-change from the same click can't queue a duplicate modal. If a modal is
          // already open: ignore a re-select of the SAME note, but for a DIFFERENT note switch
          // the open modal to it in place (messageChild) instead of close/reopen.
          if (dialogActiveRef.current) {
            if (activeDialogNoteRef.current === noteId) return;
            if (activeDialogRef.current) {
              try {
                activeDialogRef.current.messageChild(JSON.stringify({ type: "SWITCH_NOTE", noteId }));
                activeDialogNoteRef.current = noteId;
                return;
              } catch (e) {
                closeActiveNoteDialog(); // dialog gone — fall through to open a fresh one
              }
            } else {
              closeActiveNoteDialog();
            }
          }
          dialogActiveRef.current = true;
          activeDialogNoteRef.current = noteId;

          const url = new URL("dialog.html", window.location.href);
          url.searchParams.set("noteId", noteId);
          url.searchParams.set("v", Date.now().toString()); // Cache bust WebView
          Office.context.ui.displayDialogAsync(url.toString(), { height: 60, width: 50, displayInIframe: true }, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
              const dialog = asyncResult.value;
              activeDialogRef.current = dialog;
              dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
                try {
                  const data = JSON.parse(arg.message);
                  if (data.type === "READY" && graphToken) {
                    if (typeof dialog.messageChild === "function") {
                      dialog.messageChild(JSON.stringify({ type: "TOKEN", token: graphToken }));
                    } else {
                      console.warn("dialog.messageChild is not supported in this version of Office.");
                    }
                  } else if (data.type === "NOTE_UPDATED" && data.noteId) {
                    // User edited the note inside the modal — refresh the library list, and
                    // if an appendix is present, regenerate it so it reflects the new content.
                    const updatedNotes = notesRef.current.map(n => n.id === data.noteId ? { ...n, content: data.content } : n);
                    setNotes(updatedNotes);
                    if (hasAppendixRef.current) handleInsertAppendix(updatedNotes);
                  } else if (data.type === "EDIT_IN_PANE" && data.noteId) {
                    // Table notes hand off to the task-pane editor (preserves table layout).
                    // A programmatic dialog.close() does NOT fire DialogEventReceived, so we
                    // must run the same cleanup here: release the dialog gate and consume the
                    // stray post-close re-selection so the modal doesn't immediately reopen.
                    const noteId = data.noteId;
                    activeDialogRef.current = null;
                    activeDialogNoteRef.current = null;
                    dialogActiveRef.current = false;
                    suppressReopenRef.current = noteId;
                    try { dialog.close(); } catch (e) {}
                    const target = notesRef.current.find(n => n.id === noteId);
                    if (target) setTimeout(() => handleEditClick(target), 80);
                  }
                } catch (e) {}
              });
              // When the dialog closes, the caret is still sitting inside this note's
              // content control. Word only fires DocumentSelectionChanged on an actual
              // selection change, so re-clicking the same note would be a no-op. Nudge
              // the caret to just after the content control so the next click on the
              // note always registers as a fresh selection change. Keep dialogActiveRef
              // true through a short cooldown so the close-time selection events (focus
              // return + caret nudge) are suppressed regardless of which fires first.
              dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                // Only act if THIS is still the active modal — a late close event from a
                // modal we just switched away from must not clobber the new one's tracking
                // or nudge the caret (which could ping-pong the switch).
                if (activeDialogRef.current !== dialog) return;
                dialogActiveRef.current = false;          // dialog is gone — release the slot
                activeDialogRef.current = null;
                activeDialogNoteRef.current = null;
                suppressReopenRef.current = noteId;       // arm: consume the stray re-selection
                moveCaretAfterNote(noteId);               // nudge caret for repeat-click reliability
              });
            } else {
              // Open failed — release the slot so future clicks can open the modal.
              dialogActiveRef.current = false;
              activeDialogRef.current = null;
              activeDialogNoteRef.current = null;
            }
          });
        }
      }).catch(err => console.error("Selection Handler Error:", err));
    };

    if (typeof Office !== "undefined" && Office.context?.document) {
      Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, selectionHandler);
    }

    return () => {
      if (typeof Office !== "undefined" && Office.context?.document) {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, { handler: selectionHandler });
      }
    };
  }, [curDocId]);

  useEffect(() => {
    // ALWAYS fetch notes when the component mounts or user/tenant changes.
    // This ensures that the AI Prompt generator and other background tools
    // have immediate access to the current document's notes without requiring
    // the user to manually click on the "Library" tab first.
    setLoadingNotes(true);
    getLibraryNotes(user.uid, tenantId, showAllEnvironments).then(async (fetchedNotes: MarginNote[]) => {
      setNotes(fetchedNotes);
      setLoadingNotes(false);

      // Check access for any template-based note
      const lockedMap: Record<string, boolean> = {};
      for (const note of fetchedNotes) {
        if (note.templateId && note.templateId !== "tpl-general-blank") {
          try {
            const template = TEMPLATE_CATALOG.find(t => t.id === note.templateId);
            const check = await checkTemplateAccess(user.uid, note.templateId, template?.industry || "Financial");
            lockedMap[note.id] = !check.unlocked;
          } catch (err) {
            console.error("Failed to check template access for note:", note.id, err);
            lockedMap[note.id] = true; // Lock on error to be secure
          }
        }
      }
      setLockedNotes(lockedMap);
    }).catch((err: Error) => {
      console.error("Failed to fetch notes:", err);
      setLoadingNotes(false);
    });
  }, [user.uid, tenantId, showAllEnvironments]);

  // Validates that an attachment URL comes from a trusted origin before fetching.
  // Prevents SSRF by ensuring only Firebase Storage URLs are fetched client-side.
  const isTrustedAttachmentUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:") return false;
      const TRUSTED_HOSTS = [
        "firebasestorage.googleapis.com",
        "storage.googleapis.com",
      ];
      return TRUSTED_HOSTS.some(
        host => parsed.hostname === host || parsed.hostname.endsWith("." + host)
      );
    } catch {
      return false;
    }
  };

  const urlToBase64 = async (url: string): Promise<string> => {
    if (!isTrustedAttachmentUrl(url)) {
      throw new Error("Attachment URL is not from a trusted source and cannot be embedded.");
    }
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const insertWordNote = async (note: MarginNote) => {
    // Appendix lock: inserting a note into the document while the appendix is open
    // is disabled (the appendix is a generated snapshot). Remove it, insert, regenerate.
    if (hasAppendix) {
      setStatusMsg({ type: "warning", text: "The appendix is open. Remove it to add notes to the document, then regenerate it. You can still use Save Only to add notes to your Library." });
      setTimeout(() => setStatusMsg(null), 6000);
      return;
    }
    isInsertingRef.current = true;
    try {
      await Word.run(async (context) => {
        let selection = context.document.getSelection();

        // Prevent nesting: Check if we are already inside a content control
        const parentCC = selection.parentContentControlOrNullObject;
        parentCC.load("isNullObject");
        await context.sync();

        if (!parentCC.isNullObject) {
          // If inside a note, insert a space after it and move the insertion point there
          const rangeAfter = parentCC.getRange(Word.RangeLocation.after);
          rangeAfter.insertText(" ", Word.InsertLocation.replace);
          selection = rangeAfter.getRange(Word.RangeLocation.after);
        }

        selection.load("font");
        await context.sync();

        // 1. Image Embedding (if applicable)
        if (note.attachmentUrl && note.attachmentType?.startsWith('image/')) {
          try {
            const base64 = await urlToBase64(note.attachmentUrl);
            selection.insertInlinePictureFromBase64(base64, Word.InsertLocation.after);
            selection.insertText("\n", Word.InsertLocation.after);
            await context.sync();
          } catch (err) {
            console.error("Image embedding failed:", err);
          }
        }

        const fontSnapshot = [
          note.id,
          selection.font.name,
          selection.font.size,
          selection.font.color,
          selection.font.bold,
          selection.font.italic,
          selection.font.underline,
          selection.font.highlightColor || "null"
        ].join("|");

        const cc = selection.insertContentControl();
        if (hasAppendix) {
          const safeId = note.id.replace(/-/g, '_');
          const fontName = selection.font.name;
          const fontSize = selection.font.size;
          cc.insertText(note.label, Word.InsertLocation.replace);
          cc.appearance = Word.ContentControlAppearance.hidden;
          const vr = cc.getRange();
          vr.hyperlink = `#an_${safeId}`; // native internal link to the appendix entry
          vr.font.color = "#2f8570";
          vr.font.underline = Word.UnderlineType.dotted;
          if (fontName && fontName !== "null" && fontName !== "undefined") vr.font.name = fontName as any;
          if (fontSize && !isNaN(fontSize as any)) vr.font.size = fontSize as any;
          cc.getRange().insertBookmark(`dn_${safeId}`);
        } else {
          // Determine effective display mode for this note
          const noteOverride = noteDisplayOverride !== 'inherit' ? noteDisplayOverride : null;
          const effectiveMode = noteOverride ?? docDisplaySettings.mode;
          const effectiveIconId = (noteDisplayOverride === 'discreet' && noteIconOverride)
            ? noteIconOverride
            : docDisplaySettings.iconId;

          if (effectiveMode === 'discreet') {
            const icon = getIconForDocument(effectiveIconId);
            const tagParts = fontSnapshot.split('|');
            const [, fName, fSizeStr, fColor, fBold, fItalic] = tagParts;
            cc.insertHtml(
                buildDiscreetHtml(note.label, icon, fName || '', parseFloat(fSizeStr) || 12, fColor || '', fBold === 'true', fItalic === 'true'),
                Word.InsertLocation.replace
              );
              cc.font.underline = Word.UnderlineType.none;
              cc.appearance = Word.ContentControlAppearance.hidden;
          } else {
            cc.insertText(note.label, Word.InsertLocation.replace);
            const range = cc.getRange();
            cc.font.color = "#2f8570";
            cc.font.underline = Word.UnderlineType.dotted;
            cc.appearance = Word.ContentControlAppearance.hidden;
            range.font.color = "#2f8570";
            range.font.underline = Word.UnderlineType.dotted;
          }
        }

        cc.tag = fontSnapshot;
        
        // Add a space just outside the container (restores the document font for typing
        // outside the content control) plus an extra trailing space so each inserted note
        // is visually separated from the following text / the next note.
        const charAfter = cc.getRange(Word.RangeLocation.after).insertText("  ", Word.InsertLocation.replace);
        charAfter.font.name = selection.font.name;
        charAfter.font.size = selection.font.size;
        charAfter.font.color = selection.font.color;
        charAfter.font.bold = selection.font.bold;
        charAfter.font.italic = selection.font.italic;
        charAfter.font.underline = selection.font.underline;
        charAfter.font.highlightColor = selection.font.highlightColor;
        
        await context.sync();
        charAfter.select(Word.SelectionMode.end);
        await context.sync();
      });
    } catch (error) {
      console.error("insertWordNote failed:", error);
    } finally {
      isInsertingRef.current = false;
    }

    if (hasAppendix) {
      await handleInsertAppendix();
    }
  };

  /**
   * Pastes the full body of a saved note (HTML content + image attachment)
   * into the Word document at the current cursor position.
   * Unlike insertWordNote (which inserts a label marker), this dumps the
   * actual note content — tables, rich text, and images — directly into the doc.
   */
  const insertNoteContent = async (note: MarginNote) => {
    isInsertingRef.current = true;
    try {
      await Word.run(async (context) => {
        let selection = context.document.getSelection();

        // Prevent nesting: Check if we are already inside a content control
        const parentCC = selection.parentContentControlOrNullObject;
        parentCC.load("isNullObject");
        await context.sync();

        if (!parentCC.isNullObject) {
          // If inside a note, insert a space after it and move the insertion point there
          const rangeAfter = parentCC.getRange(Word.RangeLocation.after);
          rangeAfter.insertText(" ", Word.InsertLocation.replace);
          selection = rangeAfter.getRange(Word.RangeLocation.after);
        }

        // 1. Insert image attachment first (above the text/table)
        if (note.attachmentUrl && note.attachmentType?.startsWith('image/')) {
          try {
            const base64 = await urlToBase64(note.attachmentUrl);
            selection.insertInlinePictureFromBase64(base64, Word.InsertLocation.before);
            await context.sync();
          } catch (imgErr) {
            console.error("Paste: image embed failed", imgErr);
          }
        }

        // 2. Insert the note HTML body (handles both Quill HTML and template table HTML)
        if (note.content && note.content.trim() && note.content.trim() !== '<p><br></p>') {
          // Word.InsertLocation.replace replaces the current selection;
          // using 'after' keeps position just after the image (if any)
          const range = context.document.getSelection();
          range.insertHtml(note.content, Word.InsertLocation.replace);
          await context.sync();
        }
      });
      setStatusMsg({ type: "success", text: `✅ "${note.label}" pasted into document!` });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (error) {
      console.error("Paste content error:", error);
      setStatusMsg({ type: "error", text: "Could not paste content. Please click in the document first." });
      setTimeout(() => setStatusMsg(null), 4000);
    } finally {
      isInsertingRef.current = false;
    }
  };

  const validateUniqueLabel = (): boolean => {
    const trimmedLabel = label.trim().toLowerCase();
    const duplicate = notes.find(n => {
      if (n.id === editingNoteId) return false;
      if (n.label.trim().toLowerCase() !== trimmedLabel) return false;
      if (isGlobal && n.isGlobal) return true;
      if (!isGlobal && !n.isGlobal && n.documentId === curDocId) return true;
      return false;
    });

    if (duplicate) {
      setStatusMsg({ type: "error", text: isGlobal 
        ? "A Global Note with this name already exists. Please choose a unique name." 
        : "A note with this name already exists in this document. Please choose a unique name." 
      });
      setTimeout(() => setStatusMsg(null), 4000);
      return false;
    }
    return true;
  };

  const handleSaveOnly = async () => {
    if (!validateUniqueLabel()) return;
    // ── Freemium Gate ─────────────────────────────────────────────────────────
    if (!editingNoteId) {
      // Note cap: count only the user's own notes (not team notes)
      const myNoteCount = notes.filter(n => n.uid === user.uid).length;
      const capCheck = checkNoteCap(userPlan, myNoteCount);
      if (!capCheck.allowed) { setUpgradePrompt(capCheck.upgradeMessage!); return; }
    }
    if (isGlobal) {
      const globalCheck = checkGlobalNote(userPlan);
      if (!globalCheck.allowed) { setUpgradePrompt(globalCheck.upgradeMessage!); return; }
    }
    // ─────────────────────────────────────────────────────────────────────────
    try {
      const id = editingNoteId || generateUUID();
      const tId = editingNoteId 
        ? (notes.find(n => n.id === editingNoteId)?.templateId || currentTemplateId || null)
        : (currentTemplateId || null);
      const newNote: MarginNote = { id, label, content, uid: user.uid, isGlobal, visibility, documentId: curDocId, documentName: curDocName, createdAt: Date.now(), attachmentUrl, attachmentName, attachmentType, attachmentPath, excelShareUrl, templateId: tId, appendixMode };
      let updatedNotesList = notes;
      if (editingNoteId) {
        await updateNote(id, newNote);
        updatedNotesList = notes.map(n => n.id === id ? newNote : n);
        setNotes(updatedNotesList);
        setEditingNoteId(null);
        isInsertingRef.current = true;
        try {
          await syncWordNoteChanges(id, label, updatedNotesList);
        } finally {
          isInsertingRef.current = false;
        }
      } else {
        await createNote(newNote);
        setNotes(prev => [newNote, ...prev]);
      }
      const syncResult = await syncNoteToVault(newNote, graphToken);
      if (syncResult === "Success") {
        setStatusMsg({ type: "success", text: "Saved & Synced to OneDrive!" });
        setPendingSyncNote(null);
      } else if (syncResult === "Auth Required") {
        setPendingSyncNote(newNote);
        setStatusMsg({ type: "success", text: "Saved! (Cloud Sync is waiting for Login)" });
      } else if (syncResult === "Skipped") {
        setStatusMsg({ type: "success", text: "Saved to Library!" });
      } else {
        setStatusMsg({ type: "warning", text: `Saved! (Cloud Sync Issue: ${syncResult})` });
      }
      // Persist per-note display override to Custom XML
      if (noteDisplayOverride !== 'inherit') {
        const newOverrides = { ...docDisplaySettings.overrides, [id]: { mode: noteDisplayOverride as DisplayMode, iconId: noteIconOverride || docDisplaySettings.iconId } };
        const newSettings = { ...docDisplaySettings, overrides: newOverrides };
        setDocDisplaySettings(newSettings);
        await saveDocumentDisplaySettings(newSettings);
      } else if (docDisplaySettings.overrides[id]) {
        // Override removed — delete it
        const { [id]: _removed, ...rest } = docDisplaySettings.overrides;
        const newSettings = { ...docDisplaySettings, overrides: rest };
        setDocDisplaySettings(newSettings);
        await saveDocumentDisplaySettings(newSettings);
      }
      setNoteDisplayOverride('inherit'); setNoteIconOverride('');
      setLabel(""); setContent(""); setAttachmentUrl(""); setAttachmentName(""); setAttachmentType(""); setAttachmentPath(""); setExcelShareUrl(""); setAppendixMode('snapshot'); setIsGlobal(false); setVisibility("private");
      setTemplateWordHtml(null); setCurrentTemplateId(null);
      setExcelInputMode(false); setExcelUrlInput(""); setExcelWorksheets([]); setExcelDriveId(""); setExcelSheetSelection(""); setExcelRangeSelection("");
      setPptInputMode(false); setPptUrlInput("");
    } catch (e: any) {
      console.error("Save Only note failed:", e);
      setStatusMsg({ type: "error", text: `Failed to save: ${e.message || e}` });
      setTimeout(() => setStatusMsg(null), 5000);
    }
  };

  const handleCreateAndInsert = async () => {
    // Appendix lock: can't insert a NEW note into the document while the appendix is
    // open. Editing an existing note is still allowed (it re-syncs in place).
    if (!editingNoteId && hasAppendix) {
      setStatusMsg({ type: "warning", text: "The appendix is open. Remove it to add notes to the document, then regenerate it. You can still use Save Only to add notes to your Library." });
      setTimeout(() => setStatusMsg(null), 6000);
      return;
    }
    if (!validateUniqueLabel()) return;
    // ── Freemium Gate ─────────────────────────────────────────────────────────
    if (!editingNoteId) {
      const myNoteCount = notes.filter(n => n.uid === user.uid).length;
      const capCheck = checkNoteCap(userPlan, myNoteCount);
      if (!capCheck.allowed) { setUpgradePrompt(capCheck.upgradeMessage!); return; }
    }
    if (isGlobal) {
      const globalCheck = checkGlobalNote(userPlan);
      if (!globalCheck.allowed) { setUpgradePrompt(globalCheck.upgradeMessage!); return; }
    }
    // ─────────────────────────────────────────────────────────────────────────
    try {
      const id = editingNoteId || generateUUID();
      const tId = editingNoteId 
        ? (notes.find(n => n.id === editingNoteId)?.templateId || currentTemplateId || null)
        : (currentTemplateId || null);
      const newNote: MarginNote = { id, label, content, uid: user.uid, isGlobal, visibility, documentId: curDocId, documentName: curDocName, createdAt: Date.now(), attachmentUrl, attachmentName, attachmentType, attachmentPath, excelShareUrl, templateId: tId, appendixMode };
      let updatedNotesList = notes;
      if (editingNoteId) {
        await updateNote(id, newNote);
        updatedNotesList = notes.map(n => n.id === id ? newNote : n);
        setNotes(updatedNotesList);
        setEditingNoteId(null);
        await syncWordNoteChanges(id, label, updatedNotesList);
      } else {
        await createNote(newNote);
        setNotes(prev => [newNote, ...prev]);
      }
      const syncResult = await syncNoteToVault(newNote, graphToken);
      if (syncResult === "Success") {
        setStatusMsg({ type: "success", text: "Saved, Inserted & Synced!" });
        setPendingSyncNote(null);
      } else if (syncResult === "Auth Required") {
        setPendingSyncNote(newNote);
        setStatusMsg({ type: "success", text: "Saved & Inserted! (Login required for Sync)" });
      } else if (syncResult === "Skipped") {
        setStatusMsg({ type: "success", text: "Saved & Inserted!" });
      } else {
        setStatusMsg({ type: "warning", text: `Saved & Inserted! (Cloud Sync Issue: ${syncResult})` });
      }
      setTimeout(() => setStatusMsg(null), 3000);
      // Persist per-note display override
      if (noteDisplayOverride !== 'inherit') {
        const newOverrides = { ...docDisplaySettings.overrides, [id]: { mode: noteDisplayOverride as DisplayMode, iconId: noteIconOverride || docDisplaySettings.iconId } };
        const newSettings = { ...docDisplaySettings, overrides: newOverrides };
        setDocDisplaySettings(newSettings);
        await saveDocumentDisplaySettings(newSettings);
      } else if (docDisplaySettings.overrides[id]) {
        const { [id]: _removed, ...rest } = docDisplaySettings.overrides;
        const newSettings = { ...docDisplaySettings, overrides: rest };
        setDocDisplaySettings(newSettings);
        await saveDocumentDisplaySettings(newSettings);
      }
      setNoteDisplayOverride('inherit'); setNoteIconOverride('');
      setSelectedValue("library");
      await insertWordNote(newNote);
      setLabel(""); setContent(""); setAttachmentUrl(""); setAttachmentName(""); setAttachmentType(""); setAttachmentPath(""); setExcelShareUrl(""); setAppendixMode('snapshot'); setIsGlobal(false); setVisibility("private");
      setTemplateWordHtml(null); setCurrentTemplateId(null);
      setExcelInputMode(false); setExcelUrlInput(""); setExcelWorksheets([]); setExcelDriveId(""); setExcelSheetSelection(""); setExcelRangeSelection("");
      setPptInputMode(false); setPptUrlInput("");
    } catch (e: any) {
      console.error("Save & Insert note failed:", e);
      setStatusMsg({ type: "error", text: `Failed to save & insert: ${e.message || e}` });
      setTimeout(() => setStatusMsg(null), 5000);
    }
  };

  const executeExcelLoad = async (token: string, url: string) => {
    try {
      setExcelLoading(true);
      setStatusMsg(null);
      const driveId = await resolveDriveItemFromUrl(token, url);
      setExcelDriveId(driveId);
      setExcelShareUrl(url);
      const sheets = await getWorksheets(token, driveId);
      setExcelWorksheets(sheets);
      if (sheets.length > 0) setExcelSheetSelection(sheets[0].name); 
      setPendingExcelUrl(null);
      return true;
    } catch (err: any) {
      if (err.message.includes("(401)") || err.message.includes("Reconnect")) {
        setPendingExcelUrl(url);
      }
      setStatusMsg({ type: 'error', text: err.message });
      return false;
    } finally {
      setExcelLoading(false);
    }
  };

  const executePptLoad = async (token: string, url: string) => {
    try {
      setPptLoading(true);
      setStatusMsg(null);
      const meta = await resolvePresentation(token, url);
      // Persist the durable webUrl + name in the note content so the card can
      // render and "Open in PowerPoint" works even before the thumbnail loads.
      setContent(JSON.stringify({ webUrl: meta.webUrl, name: meta.name, driveItemId: meta.driveItemId }));
      setAttachmentUrl(meta.driveItemId);
      setAttachmentName(meta.name);
      setAttachmentType("powerpoint-link");
      setPptInputMode(false);
      setPptUrlInput("");
      return true;
    } catch (err: any) {
      setStatusMsg({ type: "error", text: err.message });
      return false;
    } finally {
      setPptLoading(false);
    }
  };


  const handleSimulateTeammate = async () => {
    try {
      const isMockGlobal = libraryTab === "global";
      const mockId = "mock_" + Date.now();
      const mockNote: MarginNote = {
        id: mockId,
        label: (isMockGlobal ? "[Global] " : "[Local] ") + "Teammate Research: " + mockId.slice(-4),
        content: `<p>This is a ${isMockGlobal ? 'Global' : 'Document-specific'} shared note from a colleague.</p>`,
        uid: "mock_user_123",
        tenantId: tenantId || null,
        visibility: "tenant_edit",
        isGlobal: isMockGlobal,
        documentId: isMockGlobal ? "shared_doc_global" : curDocId,
        documentName: isMockGlobal ? "Shared Document" : "My Research Doc",
        createdAt: Date.now()
      };

      await createNote(mockNote);
      setNotes(prev => [mockNote, ...prev]);
      setStatusMsg({ type: "success", text: "🚀 Success: Teammate note added!" });
      setTimeout(() => setStatusMsg(null), 5000);
    } catch (e) {
      console.error("Simulation failed:", e);
      setStatusMsg({ type: "error", text: "❌ Simulation Failed: " + (e as Error).message });
    }
  };

  const syncWordNoteChanges = async (noteId: string, newLabel: string, updatedNotes: MarginNote[]) => {
    try {
      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load("items/tag, items/text, items/appearance");
        await context.sync();

        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag !== "marginpro_appendix") {
            const parts = cc.tag.split("|");
            const id = parts[0];
            if (id === noteId) {
              if (hasAppendix) {
                const safeId = noteId.replace(/-/g, '_');
                cc.insertText(newLabel, Word.InsertLocation.replace);
                cc.appearance = Word.ContentControlAppearance.hidden;
                const vr = cc.getRange();
                vr.hyperlink = `#an_${safeId}`; // native internal link to the appendix entry
                vr.font.color = "#2f8570";
                vr.font.underline = Word.UnderlineType.dotted;
                if (parts.length > 2) {
                  const fontName = parts[1];
                  const size = parts[2];
                  if (fontName && fontName !== "null" && fontName !== "undefined") vr.font.name = fontName;
                  if (size && size !== "null" && size !== "undefined" && !isNaN(parseFloat(size))) vr.font.size = parseFloat(size);
                }
                cc.getRange().insertBookmark(`dn_${safeId}`);
              } else {
                // Determine effective display mode for this note
                const noteOverride = docDisplaySettings.overrides[noteId];
                const effectiveMode   = noteOverride ? noteOverride.mode   : docDisplaySettings.mode;
                const effectiveIconId = noteOverride ? noteOverride.iconId : docDisplaySettings.iconId;

                if (effectiveMode === 'discreet') {
                  const icon = getIconForDocument(effectiveIconId);
                  const safeIdSync = noteId.replace(/-/g, '_');
                  // Pass appendix href if appendix is active so clicking navigates to appendix
                  const hrefSync = hasAppendix ? `#an_${safeIdSync}` : undefined;
                  cc.insertHtml(
                      buildDiscreetHtml(newLabel, icon, parts[1] || '', parseFloat(parts[2]) || 12, parts[3] || '', parts[4] === 'true', parts[5] === 'true', hrefSync),
                      Word.InsertLocation.replace
                    );
                    cc.font.underline = Word.UnderlineType.none;
                    cc.appearance = Word.ContentControlAppearance.hidden;
                } else {
                  cc.insertText(newLabel, Word.InsertLocation.replace);
                  const range = cc.getRange();
                  cc.font.color = "#2f8570";
                  cc.font.underline = Word.UnderlineType.dotted;
                  cc.appearance = Word.ContentControlAppearance.hidden;
                  range.font.color = "#2f8570";
                  range.font.underline = Word.UnderlineType.dotted;
                  try {
                    if (parts.length > 2) {
                      const fontName = parts[1];
                      if (fontName && fontName !== "null" && fontName !== "undefined") {
                        cc.font.name = fontName;
                        range.font.name = fontName;
                      }
                      const size = parts[2];
                      if (size && size !== "null" && size !== "undefined" && !isNaN(parseFloat(size))) {
                        cc.font.size = parseFloat(size);
                        range.font.size = parseFloat(size);
                      }
                    }
                  } catch (e) {
                    console.error("Failed to restore font styles from tag during sync:", e);
                  }
                }
              }
            }
          }
        }
        await context.sync();
      });

      if (hasAppendix) {
        await handleInsertAppendix(updatedNotes);
      }
    } catch (err) {
      console.error("Failed to sync Word note changes:", err);
    }
  };

  const handleInsertAppendix = async (overrideNotes?: MarginNote[]) => {
    isInsertingRef.current = true;
    try {
      setStatusMsg({ type: "warning", text: "Scanning document for notes..." });

      let localNotes: MarginNote[] = [];
      await Word.run(async (context) => {
        const body = context.document.body;
        const contentControls = context.document.contentControls;
        contentControls.load("items/tag, items/text, items/title, items/appearance");
        await context.sync();

        // Check if an existing appendix Content Control is already in the document
        const existingCC = contentControls.items.find(cc => cc.tag === "marginpro_appendix");

        // Scan for active inline notes in the document to build localNotes
        const insertedNoteIds = new Set<string>();
        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag !== "marginpro_appendix") {
            const noteId = cc.tag.split("|")[0];
            insertedNoteIds.add(noteId);
          }
        }

        const notesToUse = Array.isArray(overrideNotes) ? overrideNotes : notes;
        const activeNotes = notesToUse.filter(n => insertedNoteIds.has(n.id));
        
        // Sort activeNotes based on the order of content controls in the document
        const orderMap = new Map<string, number>();
        let idx = 0;
        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag !== "marginpro_appendix") {
            const noteId = cc.tag.split("|")[0];
            if (!orderMap.has(noteId)) {
              orderMap.set(noteId, idx++);
            }
          }
        }
        activeNotes.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
        localNotes = activeNotes;

        if (localNotes.length === 0) {
          // If no active notes, delete the existing appendix if any
          if (existingCC) {
            existingCC.delete(false); // false = remove the control AND its content
            await context.sync();
          }
          return;
        }

        // ── PDF cross-links need REAL Word bookmarks as targets. Word's insertHtml does
        //    NOT promote an HTML <a name>/id anchor to a Word bookmark, and a relative
        //    #href with no matching bookmark is resolved against the document's base URL
        //    (a stray web hyperlink in the doc) — which is why links used to open a website.
        //    A #href only binds to a bookmark that already exists at insert time, so ORDER
        //    matters: (1) drop the note (dn_) bookmarks now, (2) build the appendix — its
        //    #dn_ back-links bind, and we drop real an_ bookmarks on each entry — (3) THEN
        //    convert the note markers to #an_ links, now that an_ exists.

        // Phase 1 — drop the note (dn_) bookmarks so the appendix back-links can bind.
        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag !== "marginpro_appendix") {
            const noteId = cc.tag.split("|")[0];
            if (localNotes.find(n => n.id === noteId)) {
              cc.getRange().insertBookmark(`dn_${noteId.replace(/-/g, '_')}`);
            }
          }
        }
        await context.sync();

        // Phase 2b (called after the appendix HTML is inserted) — on each entry title, set a
        // REAL Word internal hyperlink back to the note (#dn_) and drop a REAL an_ bookmark
        // (the forward target for the in-body marker). Word's range.hyperlink = "#name" is a
        // true place-in-document link (resolved against bookmarks, never a URL) — unlike an
        // HTML #href, which Word for Mac resolves against the document's base URL.
        const decorateAppendixEntries = async (appCC: Word.ContentControl) => {
          const paras = appCC.getRange().paragraphs;
          paras.load("items/text");
          await context.sync();
          // ponytail: matches title paragraphs by exact label text in note order — fine
          // unless a note's body paragraph equals its label; switch to per-entry insertion if that bites.
          let i = 0;
          for (const para of paras.items) {
            if (i >= localNotes.length) break;
            const label = localNotes[i].label.trim();
            if (label && para.text.trim() === label) {
              const safeId = localNotes[i].id.replace(/-/g, '_');
              const pr = para.getRange();
              pr.hyperlink = `#dn_${safeId}`;     // back-link to the note in the body
              pr.font.color = "#2f8570";          // re-assert styling over the Hyperlink char style
              pr.font.bold = true;
              pr.insertBookmark(`an_${safeId}`);  // forward target for the in-body marker
              i++;
            }
          }
          await context.sync();
        };

        // Phase 3 (called after the appendix exists) — point each in-body note marker at its
        // appendix entry via a real internal hyperlink (#an_), then re-drop its dn_ bookmark
        // (insertHtml/insertText replace wipes the bookmark living inside the control).
        const convertNoteLinksToAppendix = async () => {
          for (const cc of contentControls.items) {
            if (cc.tag && cc.tag !== "marginpro_appendix") {
              const parts = cc.tag.split("|");
              const noteId = parts[0];
              const matchingNote = localNotes.find(n => n.id === noteId);
              if (matchingNote) {
                const safeId = noteId.replace(/-/g, '_');
                const noteOverrideApp  = docDisplaySettings.overrides[noteId];
                const effectiveModeApp = noteOverrideApp ? noteOverrideApp.mode   : docDisplaySettings.mode;
                const effectiveIconApp = noteOverrideApp ? noteOverrideApp.iconId : docDisplaySettings.iconId;

                if (effectiveModeApp === 'discreet') {
                  const icon = getIconForDocument(effectiveIconApp);
                  cc.insertHtml(
                      buildDiscreetHtml(matchingNote.label, icon, parts[1] || '', parseFloat(parts[2]) || 12, parts[3] || '', parts[4] === 'true', parts[5] === 'true'),
                      Word.InsertLocation.replace
                    );
                    cc.font.underline = Word.UnderlineType.none;
                    cc.appearance = Word.ContentControlAppearance.hidden;
                  const dr = cc.getRange();
                  dr.hyperlink = `#an_${safeId}`;          // internal link; inline span colors keep the discreet look
                  cc.getRange().insertBookmark(`dn_${safeId}`);
                } else {
                  // Visible mode — green dotted marker that links to its appendix entry
                  cc.insertText(matchingNote.label, Word.InsertLocation.replace);
                  cc.appearance = Word.ContentControlAppearance.hidden;
                  const vr = cc.getRange();
                  vr.hyperlink = `#an_${safeId}`;          // set the link first
                  vr.font.color = "#2f8570";               // then override the Hyperlink char style
                  vr.font.underline = Word.UnderlineType.dotted;
                  if (parts.length > 2) {
                    const fontName = parts[1];
                    const size = parts[2];
                    if (fontName && fontName !== "null" && fontName !== "undefined") vr.font.name = fontName;
                    const sz = parseFloat(size);
                    if (size && size !== "null" && size !== "undefined" && !isNaN(sz)) vr.font.size = sz;
                  }
                  cc.getRange().insertBookmark(`dn_${safeId}`);
                }
              }
            }
          }
          await context.sync();
        };

        // Re-hydrate Excel notes' snapshot data from Firestore. ExcelLiveNote
        // persists freshly synced values to the DB but does not refresh the parent
        // `notes` state, so the in-memory copy here can be stale ("not captured")
        // right after a sync. Pull the latest saved content so the snapshot is current.
        localNotes = await Promise.all(
          localNotes.map(async (n) => {
            if (n.attachmentType === 'excel-live-link') {
              try {
                const fresh = await getNote(n.id);
                if (fresh?.content) return { ...n, content: fresh.content };
              } catch { /* keep in-memory copy on failure */ }
            }
            return n;
          })
        );

        // Pre-fetch PPT cover-slide thumbnail URLs for snapshot-mode PowerPoint
        // notes. We hand the URL to an <img> in the appendix and let WORD fetch +
        // embed it at insert time — Word isn't bound by the add-in CSP (which would
        // block a cross-origin fetch of a OneDrive CDN thumbnail), and it bakes the
        // image into the document, so the short-lived URL expiring later is harmless.
        const pptThumbs = new Map<string, string>();
        if (graphToken) {
          for (const note of localNotes) {
            const mode = note.appendixMode || 'snapshot';
            if (note.attachmentType === 'powerpoint-link' && mode !== 'link' && note.attachmentUrl) {
              const thumbUrl = await fetchCoverThumbnail(graphToken, note.attachmentUrl);
              if (thumbUrl) pptThumbs.set(note.id, thumbUrl);
            }
          }
        }

        // Build the complete, beautiful, and self-contained HTML for the entire appendix contents
        let appendixHtml = `<div style="font-family: 'Segoe UI', sans-serif;">`;
        appendixHtml += `<p style="color: #2f8570; font-size: 22pt; font-weight: bold; margin-top: 0; margin-bottom: 4px;">Appendix</p>`;
        appendixHtml += `<p style="color: #6b7280; font-size: 9pt; font-style: italic; margin-bottom: 4px;">💡 Tip: Click any Note title below to jump to its location in the document. Click the Note marker in the document to jump back here.</p>`;
        appendixHtml += `<p style="color: #9ca3af; font-size: 8.5pt; font-style: italic; margin-bottom: 24px; padding: 6px 10px; background-color: #f9fafb; border-left: 3px solid #e5e7eb;">⚠️ This appendix is a snapshot generated by MarginPro. Any edits made directly in this appendix will not be reflected in the corresponding notes. To update content, edit the note in MarginPro and regenerate the appendix.</p>`;

        for (const note of localNotes) {
          const safeId = note.id.replace(/-/g, '_');
          appendixHtml += `<div style="margin-top: 24px; margin-bottom: 16px; page-break-inside: avoid;">`;
          // Plain title paragraph (bare label so the matcher in decorateAppendixEntries finds
          // it). The internal hyperlink (#dn_ back-link) and the an_ bookmark are applied there
          // via the Word API — HTML #hrefs get resolved against the doc base URL on Mac.
          appendixHtml += `<p style="margin-bottom: 4px; color: #2f8570; font-size: 12pt; font-weight: bold;">${note.label}</p>`;

          if (note.attachmentType === 'excel-live-link') {
            const rangeLabel = note.attachmentPath === 'auto' ? 'Full Range' : note.attachmentPath;
            appendixHtml += `<p style="font-style: italic; color: #59766C; font-size: 10pt; margin-top: 0; margin-bottom: 8px;">📊 Excel: ${note.attachmentName} — ${rangeLabel}</p>`;
          } else if (note.attachmentType === 'powerpoint-link') {
            appendixHtml += `<p style="font-style: italic; color: #59766C; font-size: 10pt; margin-top: 0; margin-bottom: 8px;">📽️ PowerPoint: ${note.attachmentName || 'Presentation'}</p>`;
          } else if (note.attachmentUrl) {
            appendixHtml += `<p style="font-style: italic; color: #59766C; font-size: 10pt; margin-top: 0; margin-bottom: 8px;">📎 Attachment: <a href="${note.attachmentUrl}" style="color: #2f8570; text-decoration: underline;">${note.attachmentName || 'View File'}</a></p>`;
          }

          // Note Content
          if (note.attachmentType === 'excel-live-link') {
            const exMode = note.appendixMode || 'snapshot';
            if (exMode === 'link' && isSafeWebUrl(note.excelShareUrl)) {
              // Share-link mode — link to the live file (user opted in; permissions follow OneDrive).
              appendixHtml += `<div style="font-size: 10pt; color: #333; padding: 10px 12px; background: #f8fafb; border-left: 3px solid #2f8570; margin: 8px 0 16px 0;">🔗 <a href="${note.excelShareUrl}" style="color: #2f8570; text-decoration: underline;">Open the live Excel file in OneDrive</a><br/><span style="font-size: 8.5pt; color: #6b7280; font-style: italic;">Opens the original file; access follows its OneDrive sharing permissions.</span></div>`;
            } else {
            // ── Secure static preview — no OneDrive URL in the appendix ──────────
            // Shows header row + up to 8 data rows with truncated cell text.
            // Cell values are capped at 30 chars to prevent column blow-out.
            // Full data is always available in MarginPro → Sync.
            const cached = parseExcelContent(note.content);
            if (cached?.data?.values?.length) {
              const allRows = cached.data.values;
              const totalRows = allRows.length;
              const totalCols = allRows[0]?.length ?? 0;
              const PREVIEW_ROWS = 8; // header + 7 data rows
              const MAX_CELL_LEN = 30;
              const capturedDate = cached.syncedAt
                ? new Date(cached.syncedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                : null;

              const truncate = (val: any): string => {
                const s = String(val ?? '');
                return s.length > MAX_CELL_LEN ? s.slice(0, MAX_CELL_LEN) + '…' : s;
              };

              const PREVIEW_COLS = 6; // max columns in appendix preview
              const previewCols = Math.min(totalCols, PREVIEW_COLS);
              const hiddenCols  = totalCols - previewCols;

              // Slice both rows and columns
              const previewRows = allRows.slice(0, PREVIEW_ROWS + 1).map(
                (row: any[]) => row.slice(0, previewCols)
              );
              const hiddenRows = totalRows - PREVIEW_ROWS;

              // Calculate content-driven column widths:
              // For each column, measure the longest value across header + preview rows,
              // then clamp between 60 px and 140 px.
              const colWidths = Array.from({ length: previewCols }, (_, ci) => {
                const maxLen = previewRows.reduce((m: number, row: any[]) =>
                  Math.max(m, String(row[ci] ?? '').length), 0);
                return Math.max(60, Math.min(maxLen * 7, 140));
              });

              appendixHtml += `<div style="margin-top: 8px; margin-bottom: 4px;">`;
              // No width:100% — let Word size the table to content, then scale to page
              appendixHtml += `<table style="border-collapse: collapse; font-size: 9pt; font-family: 'Calibri', sans-serif; table-layout: auto;">`;

              // colgroup sets minimum widths per column so headers never collapse
              appendixHtml += `<colgroup>`;
              colWidths.forEach(w => {
                appendixHtml += `<col style="min-width: ${w}px;" />`;
              });
              appendixHtml += `</colgroup>`;

              previewRows.forEach((row: any[], rowIdx: number) => {
                appendixHtml += `<tr>`;
                row.forEach((cell: any) => {
                  const cellText = truncate(cell);
                  if (rowIdx === 0) {
                    appendixHtml += `<th style="background-color: #2f8570; color: white; font-weight: bold; padding: 5px 10px; border: 1px solid #1e5c4d; text-align: left; white-space: nowrap;">${cellText}</th>`;
                  } else {
                    const bg = rowIdx % 2 === 0 ? '#f4f8f7' : 'white';
                    appendixHtml += `<td style="padding: 4px 10px; border: 1px solid #e0e0e0; background-color: ${bg}; white-space: nowrap;">${cellText}</td>`;
                  }
                });
                appendixHtml += `</tr>`;
              });
              appendixHtml += `</table>`;

              // Summary line
              const summaryParts: string[] = [`${totalRows} rows × ${totalCols} columns`];
              if (hiddenRows > 0) summaryParts.push(`${hiddenRows} rows not shown`);
              if (hiddenCols > 0) summaryParts.push(`${hiddenCols} columns not shown`);
              if (capturedDate)   summaryParts.push(`captured ${capturedDate}`);
              appendixHtml += `<p style="font-size: 8pt; color: #6b7280; margin: 5px 0 0 0; font-style: italic;">`;
              appendixHtml += summaryParts.join(' · ');
              appendixHtml += ` · Open in MarginPro and click Sync to refresh.</p>`;
              appendixHtml += `</div>`;
            } else {
              appendixHtml += `<div style="font-size: 10pt; color: #6b7280; font-style: italic; padding: 12px; background: #f8fafb; border-left: 3px solid #d1d5db; margin: 8px 0 16px 0;">`;
              appendixHtml += `Excel data not yet captured. Open this note in MarginPro, click the sync button, then regenerate the appendix.`;
              appendixHtml += `</div>`;
            }
            } // end snapshot/else for excel appendixMode
          } else if (note.attachmentType === 'powerpoint-link') {
            const ppMode = note.appendixMode || 'snapshot';
            let ppWebUrl = '';
            try { ppWebUrl = JSON.parse(note.content || '{}')?.webUrl || ''; } catch { /* ignore */ }
            if (ppMode === 'link' && isSafeWebUrl(ppWebUrl)) {
              // Share-link mode — link to the original deck (permissions follow OneDrive).
              appendixHtml += `<div style="font-size: 10pt; color: #333; padding: 10px 12px; background: #f8fafb; border-left: 3px solid #2f8570; margin: 8px 0 16px 0;">🔗 <a href="${ppWebUrl}" style="color: #2f8570; text-decoration: underline;">Open the PowerPoint in OneDrive</a><br/><span style="font-size: 8.5pt; color: #6b7280; font-style: italic;">Opens the original deck; access follows its OneDrive sharing permissions.</span></div>`;
            } else if (pptThumbs.has(note.id)) {
              // Snapshot mode — baked-in cover-slide thumbnail.
              // Word ignores CSS max-width on inserted images and uses the thumbnail's
              // full intrinsic size, which overflows the text column. Pin an explicit
              // width (~5.6in) that fits inside standard page margins; height auto keeps aspect.
              appendixHtml += `<div style="margin: 8px 0 16px 0;"><img src="${pptThumbs.get(note.id)}" alt="Cover slide" width="470" style="width: 470px; max-width: 100%; height: auto; border: 1px solid #e0e0e0; border-radius: 4px;" /><p style="font-size: 8pt; color: #6b7280; margin: 4px 0 0 0; font-style: italic;">Cover slide snapshot · Open in MarginPro to preview all slides.</p></div>`;
            } else {
              // Snapshot requested but no thumbnail available (e.g. OneDrive not connected).
              appendixHtml += `<div style="font-size: 10pt; color: #6b7280; font-style: italic; padding: 12px; background: #f8fafb; border-left: 3px solid #d1d5db; margin: 8px 0 16px 0;">Linked PowerPoint presentation — open this note in MarginPro to preview the slides or open the full deck in PowerPoint.</div>`;
            }
          } else {
            appendixHtml += `<div style="font-size: 10pt; color: #333; line-height: 1.4; margin-bottom: 12px;">${note.content}</div>`;
          }
          appendixHtml += `</div>`;
        }
        appendixHtml += `</div>`;

        if (existingCC) {
          // UPDATE IN-PLACE: Simply overwrite the contents of the existing appendix content control
          existingCC.insertHtml(appendixHtml, Word.InsertLocation.replace);
          existingCC.insertBreak(Word.BreakType.page, Word.InsertLocation.start);
          await context.sync();
          await decorateAppendixEntries(existingCC);
          await convertNoteLinksToAppendix();
        } else {
          // CREATE FRESH:
          // 1. Search and delete any leftover stray marker paragraphs [MP_APP] or headings
          const markerResults = body.search("[MP_APP]", { matchCase: true });
          markerResults.load("items");
          await context.sync();
          if (markerResults.items.length > 0) {
            for (const item of markerResults.items) {
              const rangeToDelete = item.expandTo(body.getRange(Word.RangeLocation.end));
              rangeToDelete.delete();
            }
            await context.sync();
          }

          // Anchor on the unique disclaimer sentence — bare "Appendix" could match user text.
          const searchResults = body.search("snapshot generated by MarginPro", { matchCase: true });
          searchResults.load("items");
          await context.sync();
          if (searchResults.items.length > 0) {
            for (const item of searchResults.items) {
              const rangeToDelete = item.expandTo(body.getRange(Word.RangeLocation.end));
              rangeToDelete.delete();
            }
            await context.sync();
          }

          // 2. Create the placeholder paragraph for the content control directly at the end of the body
          const appendixStartPara = body.insertParagraph("", Word.InsertLocation.end);
          
          // 3. Wrap starting from the appendixStartPara to the end of the body in the content control
          const appendixRange = appendixStartPara.getRange().expandTo(body.getRange(Word.RangeLocation.end));
          const appendixCC = appendixRange.insertContentControl();
          appendixCC.title = "MarginPro Appendix";
          appendixCC.tag = "marginpro_appendix";
          appendixCC.appearance = Word.ContentControlAppearance.hidden;

          // 4. Insert the HTML content inside the new content control
          appendixCC.insertHtml(appendixHtml, Word.InsertLocation.replace);

          // 5. Insert the page break inside at the very start of the content control
          appendixCC.insertBreak(Word.BreakType.page, Word.InsertLocation.start);
          await context.sync();
          await decorateAppendixEntries(appendixCC);
          await convertNoteLinksToAppendix();
        }
      });

      if (localNotes.length === 0) {
        setStatusMsg({ type: "warning", text: "No active notes found in the document text to create an appendix." });
        setHasAppendix(false);
      } else {
        setHasAppendix(true);
        setHadAppendix(true);
        setStatusMsg({ type: "success", text: "Appendix successfully synchronized!" });
      }
      setTimeout(() => setStatusMsg(null), 5000);
    } catch (err: any) {
      console.error("Failed to insert appendix:", err);
      setStatusMsg({ type: "error", text: "Failed to generate appendix: " + err.message });
    } finally {
      // Delay clearing the inserting guard — Word delivers DocumentSelectionChanged
      // events asynchronously after bulk content-control writes, so they can arrive
      // after the finally block runs. The 400ms window lets those queued events
      // pass while the guard is still active, preventing a spurious modal open.
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  const handleRemoveAppendix = async () => {
    isInsertingRef.current = true;
    try {
      setStatusMsg({ type: "warning", text: "Removing appendix and restoring editing mode..." });

      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        const body = context.document.body;
        contentControls.load("items/tag, items/text");
        await context.sync();

        // 1. Delete any existing appendix and marker Content Controls first safely
        const toDeleteApp = contentControls.items.filter(cc => cc.tag === "marginpro_appendix");
        for (const cc of toDeleteApp) {
          cc.delete(false); // false = remove the control AND all nested content (header, tables, page break). delete(true) KEEPS the content — that was the leftover-header bug.
        }
        await context.sync();

        const toDeleteMarker = contentControls.items.filter(cc => cc.tag === "marginpro_marker");
        for (const cc of toDeleteMarker) {
          cc.delete(false); // false = remove the control AND its page break
        }
        await context.sync();

        // 2. Search and delete any stray marker paragraph "[MP_APP]"
        const markerResults = body.search("[MP_APP]", { matchCase: true });
        markerResults.load("items");
        await context.sync();
        if (markerResults.items.length > 0) {
          for (const item of markerResults.items) {
            const rangeToDelete = item.expandTo(body.getRange(Word.RangeLocation.end));
            rangeToDelete.delete();
          }
          await context.sync();
        }

        // 3. Secondary search-and-destroy backup: anchor on the unique disclaimer
        //    sentence (bare "Appendix" could match user text) and clear to end.
        const searchResults = body.search("snapshot generated by MarginPro", { matchCase: true });
        searchResults.load("items");
        await context.sync();
        
        if (searchResults.items.length > 0) {
          for (const item of searchResults.items) {
            const rangeToDelete = item.expandTo(body.getRange(Word.RangeLocation.end));
            rangeToDelete.delete();
          }
          await context.sync();
        }

        // 4. Clean up trailing empty paragraphs at the end of the document body (acts like automatic Backspacing)
        const paragraphs = body.paragraphs;
        paragraphs.load("items/text");
        await context.sync();

        for (let i = paragraphs.items.length - 1; i > 0; i--) {
          const para = paragraphs.items[i];
          if (!para.text || para.text.trim() === "") {
            para.delete();
          } else {
            break;
          }
        }
        await context.sync();

        // 3. Convert all remaining inline notes back to clean plain text (removing the hyperlinks)
        contentControls.load("items/tag, items/text");
        await context.sync();

        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag !== "marginpro_appendix") {
            const parts = cc.tag.split("|");
            const noteId = parts[0];

            // Remove any appendix hyperlink (#an_) left on the marker. Replacing the
            // content below does NOT reliably strip a range-level hyperlink, so clear it
            // explicitly — otherwise a click follows the now-deleted appendix bookmark and
            // the "open note" selection handler never fires (marker appears dead).
            try { cc.getRange().hyperlink = ""; } catch (e) { /* no hyperlink present */ }

            // Use the note's stored label — cc.text includes the icon char for discreet notes
            const matchingNote = notesRef.current.find(n => n.id === noteId);
            const labelText = matchingNote ? matchingNote.label : cc.text;

            // Check effective display mode for this note
            const noteOverrideRem  = docDisplaySettings.overrides[noteId];
            const effectiveModeRem = noteOverrideRem ? noteOverrideRem.mode   : docDisplaySettings.mode;
            const effectiveIconRem = noteOverrideRem ? noteOverrideRem.iconId : docDisplaySettings.iconId;

            if (effectiveModeRem === 'discreet') {
              const icon = getIconForDocument(effectiveIconRem);
              cc.insertHtml(
                buildDiscreetHtml(labelText, icon, parts[1] || '', parseFloat(parts[2]) || 12, parts[3] || '', parts[4] === 'true', parts[5] === 'true'),
                Word.InsertLocation.replace
              );
              cc.font.underline = Word.UnderlineType.none;
              cc.appearance = Word.ContentControlAppearance.hidden;
            } else {
              // Visible mode — restore green dotted underline styling
              cc.insertText(labelText, Word.InsertLocation.replace);
              const range = cc.getRange();
              cc.font.color = "#2f8570";
              cc.font.underline = Word.UnderlineType.dotted;
              cc.appearance = Word.ContentControlAppearance.hidden;
              range.font.color = "#2f8570";
              range.font.underline = Word.UnderlineType.dotted;
              try {
                if (parts.length > 2) {
                  const fontName = parts[1];
                  if (fontName && fontName !== "null" && fontName !== "undefined") {
                    cc.font.name = fontName;
                    range.font.name = fontName;
                  }
                  const size = parts[2];
                  if (size && size !== "null" && size !== "undefined" && !isNaN(parseFloat(size))) {
                    cc.font.size = parseFloat(size);
                    range.font.size = parseFloat(size);
                  }
                }
              } catch (e) {
                console.error("Failed to restore font styles from tag metadata:", e);
              }
            }
          }
        }

        await context.sync();
      });

      setHasAppendix(false);
      setStatusMsg({ type: "success", text: "Appendix removed and notes restored to edit mode!" });
      setTimeout(() => setStatusMsg(null), 5000);
    } catch (err: any) {
      console.error("Failed to remove appendix:", err);
      setStatusMsg({ type: "error", text: "Failed to remove appendix: " + err.message });
    } finally {
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  const handleReconnect = async () => {
    try {
      setStatusMsg({ type: "success", text: "Connecting OneDrive..." });

      // The user is already signed in — we only need a fresh Graph token.
      // For PERSONAL (MSA) accounts, Office SSO/OBO isn't available, and
      // loginWithSSO() would fall back to a full sign-in DIALOG — causing a
      // second Microsoft prompt on top of the Graph dialog (the "enter email
      // twice" bug). So personal accounts go straight to the single Graph dialog.
      let resolvedToken: string | null = null;
      if (!isPersonalAccount) {
        // Work/school: try silent OBO first (no prompt when SSO is available).
        try {
          const result = await loginWithSSO();
          resolvedToken = result.graphToken;
        } catch (_) { /* fall through to dialog */ }
      }

      // Interactive PKCE dialog — single Microsoft prompt (personal accounts).
      if (!resolvedToken) {
        resolvedToken = await getGraphTokenViaDialog();
      }

      if (resolvedToken) {
        setHasSyncToken(true);
        setGraphToken(resolvedToken);
        localStorage.setItem('marginpro_graph_token', resolvedToken);
        await ensureUserProfile(user, null, resolvedToken);
        setStatusMsg({ type: "success", text: "OneDrive Connected! ✅" });
        setTimeout(() => setStatusMsg(null), 5000);
      }
    } catch (err: any) {
      console.error("Reconnect failed:", err);
      setStatusMsg({ type: "error", text: "Failed to connect OneDrive: " + err.message });
    }
  };

  // Re-sync every note to the OneDrive vault. Migrates older notes from the legacy
  // .md to the indexable .txt format (syncNoteToVault writes .txt and removes the old
  // .md), so Microsoft 365 Copilot can find the whole vault, not just new notes.
  const handleResyncVault = async () => {
    if (!graphToken) {
      setStatusMsg({ type: "warning", text: "Connect OneDrive first, then re-sync." });
      return;
    }
    const all = notesRef.current;
    if (all.length === 0) {
      setStatusMsg({ type: "warning", text: "No notes to sync yet." });
      return;
    }
    setResyncing(true);
    setStatusMsg({ type: "success", text: `Re-syncing ${all.length} notes to OneDrive...` });
    let ok = 0, fail = 0;
    for (const note of all) {
      const res = await syncNoteToVault(note, graphToken);
      if (res === "Success") ok++; else fail++;
    }
    setResyncing(false);
    setStatusMsg({
      type: fail === 0 ? "success" : "warning",
      text: fail === 0
        ? `Synced ${ok} notes to OneDrive as .txt ✅`
        : `Synced ${ok}, ${fail} failed. Reconnect OneDrive, then re-sync.`,
    });
    setTimeout(() => setStatusMsg(null), 6000);
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenantId) return;

    setImportingCSV(true);
    setStatusMsg({ type: "warning", text: "Parsing CSV & Enrolling Members..." });

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        // Robust Regex to find emails in the CSV
        const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
        const matches = text.match(emailRegex) || [];
        
        if (matches.length === 0) {
          setStatusMsg({ type: "error", text: "No valid emails found in the CSV." });
          setImportingCSV(false);
          return;
        }

        const uniqueEmails = Array.from(new Set(matches.map(e => e.toLowerCase())));
        await bulkAddToWhitelist(tenantId, uniqueEmails);

        setStatusMsg({ type: "success", text: `Success: ${uniqueEmails.length} pilot members enrolled!` });
        setAdminWhitelist(prev => Array.from(new Set([...prev, ...uniqueEmails])));
      } catch (err) {
        console.error("CSV Import Failed:", err);
        setStatusMsg({ type: "error", text: "Import Failed. Please check the file format." });
      } finally {
        setImportingCSV(false);
        if (csvInputRef.current) csvInputRef.current.value = "";
        setTimeout(() => setStatusMsg(null), 5000);
      }
    };
    reader.readAsText(file);
  };

  // ─── Display Mode: Apply to Document ──────────────────────────────────────
  const applyDisplayModeToDocument = async () => {
    setApplyingDisplayMode(true);
    isInsertingRef.current = true;
    try {
      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load("items/tag");
        await context.sync();

        for (const cc of contentControls.items) {
          if (!cc.tag || cc.tag === 'marginpro_appendix' || cc.tag === 'marginpro_marker') continue;
          const parts = cc.tag.split('|');
          const noteId = parts[0];
          if (!noteId || noteId.length < 8) continue;

          // Per-note override handling
          const hasOverride = !!docDisplaySettings.overrides[noteId];
          if (hasOverride && !includeOverridesInApply) continue;

          const note = notesRef.current.find(n => n.id === noteId);
          if (!note) continue;

          const effectiveMode   = (hasOverride && !includeOverridesInApply)
            ? docDisplaySettings.overrides[noteId].mode
            : docDisplaySettings.mode;
          const effectiveIconId = (hasOverride && !includeOverridesInApply)
            ? docDisplaySettings.overrides[noteId].iconId
            : docDisplaySettings.iconId;

          const [, fontName, fontSizeStr, fontColor, fontBoldStr, fontItalicStr] = parts;

          // When an appendix exists, every note must keep its two-way PDF navigation
          // link (#an_<id> anchor + dn_<id> bookmark). Switching display mode must NOT
          // strip that, or the in-doc note stops jumping to its appendix entry.
          const appendixActive = hasAppendixRef.current;
          const safeId = noteId.replace(/-/g, '_');

          if (effectiveMode === 'discreet') {
            const icon = getIconForDocument(effectiveIconId);
            cc.insertHtml(
              buildDiscreetHtml(
                note.label, icon, fontName || '', parseFloat(fontSizeStr) || 12,
                fontColor || '', fontBoldStr === 'true', fontItalicStr === 'true',
              ),
              Word.InsertLocation.replace
            );
            cc.font.underline = Word.UnderlineType.none;
            cc.appearance = Word.ContentControlAppearance.hidden;
            if (appendixActive) {
              cc.getRange().hyperlink = `#an_${safeId}`; // native internal link, not an HTML #href
              cc.getRange().insertBookmark(`dn_${safeId}`);
            }
          } else if (appendixActive) {
            // Visible mode WITH appendix — green marker linked to its appendix entry
            cc.insertText(note.label, Word.InsertLocation.replace);
            cc.appearance = Word.ContentControlAppearance.hidden;
            const vr = cc.getRange();
            vr.hyperlink = `#an_${safeId}`;
            vr.font.color = "#2f8570";
            vr.font.underline = Word.UnderlineType.dotted;
            if (fontName && fontName !== "null" && fontName !== "undefined") vr.font.name = fontName;
            if (fontSizeStr && fontSizeStr !== "null" && fontSizeStr !== "undefined" && !isNaN(parseFloat(fontSizeStr))) vr.font.size = parseFloat(fontSizeStr);
            cc.getRange().insertBookmark(`dn_${safeId}`);
          } else {
            cc.insertText(note.label, Word.InsertLocation.replace);
            cc.font.color = '#2f8570';
            cc.font.underline = Word.UnderlineType.dotted;
            cc.appearance = Word.ContentControlAppearance.hidden;
            const range = cc.getRange();
            range.font.color = '#2f8570';
            range.font.underline = Word.UnderlineType.dotted;
          }
        }
        await context.sync();
      });

      // If wiping overrides, clear them from settings
      const newSettings: DocumentDisplaySettings = includeOverridesInApply
        ? { ...docDisplaySettings, overrides: {} }
        : { ...docDisplaySettings };
      setDocDisplaySettings(newSettings);
      await saveDocumentDisplaySettings(newSettings);
      setStatusMsg({ type: 'success', text: 'Display mode applied to document.' });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: `Failed to apply: ${err.message}` });
    } finally {
      setApplyingDisplayMode(false);
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  // Effective display mode for one note (per-note override falls back to document mode).
  const noteEffectiveMode = (noteId: string): DisplayMode =>
    docDisplaySettings.overrides[noteId]?.mode || docDisplaySettings.mode;

  // Toggle a single note between Visible/Discreet from the Library: persists a per-note
  // override and re-renders just that note's content control in the open document
  // (keeping the appendix link/bookmark when an appendix is active).
  const setNoteDisplayMode = async (note: MarginNote, mode: DisplayMode) => {
    const iconId = docDisplaySettings.overrides[note.id]?.iconId || docDisplaySettings.iconId;
    const newOverrides = { ...docDisplaySettings.overrides, [note.id]: { mode, iconId } };
    const newSettings: DocumentDisplaySettings = { ...docDisplaySettings, overrides: newOverrides };
    setDocDisplaySettings(newSettings);
    await saveDocumentDisplaySettings(newSettings);

    isInsertingRef.current = true;
    try {
      await Word.run(async (context) => {
        const ccs = context.document.contentControls;
        ccs.load("items/tag");
        await context.sync();
        const cc = ccs.items.find(c => c.tag && c.tag.split('|')[0] === note.id && c.tag !== 'marginpro_appendix');
        if (!cc) return; // note not inserted in this document — override applies on next insert
        const parts = cc.tag.split('|');
        const [, fontName, fontSizeStr, fontColor, fontBoldStr, fontItalicStr] = parts;
        const appendixActive = hasAppendixRef.current;
        const safeId = note.id.replace(/-/g, '_');

        // Determine the SURROUNDING body-text formatting so the note inherits its context
        // rather than the note's own (possibly stale) font. The boundary right next to a
        // content control reports the NOTE's font, not the body text — so instead we tally
        // the font across all the words of the note's paragraph and take the most common
        // value. The note is a small minority, so the dominant size/family is the real
        // surrounding body text. Falls back to the stored snapshot if the paragraph can't
        // be read (e.g. the note is alone in its paragraph).
        const numOk = (v: any) => typeof v === 'number' && !isNaN(v) && v > 0;
        const valid = (v: any) => v !== null && v !== undefined && v !== '' && v !== 'null' && v !== 'undefined';
        const pickMode = (counts: Record<string, number>): string | null => {
          let best: string | null = null, n = -1;
          for (const k in counts) { if (counts[k] > n) { n = counts[k]; best = k; } }
          return best;
        };
        let mName: any = null, mSize: any = null, mBold: any = null, mItalic: any = null;
        try {
          const paraRange = cc.getRange().paragraphs.getFirst().getRange();
          const words = paraRange.getTextRanges([" "], true);
          words.load("items/font/name,items/font/size,items/font/bold,items/font/italic");
          await context.sync();
          const sizeC: Record<string, number> = {}, nameC: Record<string, number> = {}, boldC: Record<string, number> = {}, italicC: Record<string, number> = {};
          for (const w of words.items) {
            const f = w.font;
            if (numOk(f.size)) sizeC[String(f.size)] = (sizeC[String(f.size)] || 0) + 1;
            if (valid(f.name)) nameC[f.name] = (nameC[f.name] || 0) + 1;
            boldC[String(!!f.bold)] = (boldC[String(!!f.bold)] || 0) + 1;
            italicC[String(!!f.italic)] = (italicC[String(!!f.italic)] || 0) + 1;
          }
          const msz = pickMode(sizeC); if (msz !== null) mSize = parseFloat(msz);
          mName = pickMode(nameC);
          const mb = pickMode(boldC); if (mb !== null) mBold = (mb === 'true');
          const mi = pickMode(italicC); if (mi !== null) mItalic = (mi === 'true');
        } catch (e) { /* fall back to the stored snapshot below */ }
        const sName = valid(mName) ? mName : (fontName || '');
        const sSize = numOk(mSize) ? mSize : (parseFloat(fontSizeStr) || 12);
        const sColor = valid(fontColor) ? fontColor : '';
        const sBold = typeof mBold === 'boolean' ? mBold : (fontBoldStr === 'true');
        const sItalic = typeof mItalic === 'boolean' ? mItalic : (fontItalicStr === 'true');
        // Refresh the stored snapshot so future toggles stay in sync with the surrounding font.
        const freshTag = `${note.id}|${sName}|${sSize}|${sColor}|${sBold}|${sItalic}`;

        if (mode === 'discreet') {
          const icon = getIconForDocument(iconId);
          cc.insertHtml(
            buildDiscreetHtml(note.label, icon, sName, sSize, sColor, sBold, sItalic),
            Word.InsertLocation.replace
          );
          cc.font.underline = Word.UnderlineType.none;
          cc.appearance = Word.ContentControlAppearance.hidden;
          // Word's insertHtml doesn't reliably apply the inline pt font-size to a content
          // control's text, so force the surrounding size/family via the API (same as the
          // Visible branch) to keep the discreet label matching the body text.
          const dRange = cc.getRange();
          if (typeof sSize === 'number' && !isNaN(sSize) && sSize > 0) dRange.font.size = sSize;
          if (valid(sName)) dRange.font.name = sName;
          if (appendixActive) {
            cc.getRange().hyperlink = `#an_${safeId}`; // native internal link, not an HTML #href
            cc.getRange().insertBookmark(`dn_${safeId}`);
          }
        } else if (appendixActive) {
          cc.insertText(note.label, Word.InsertLocation.replace);
          cc.appearance = Word.ContentControlAppearance.hidden;
          const vr = cc.getRange();
          vr.hyperlink = `#an_${safeId}`;
          vr.font.color = "#2f8570";
          vr.font.underline = Word.UnderlineType.dotted;
          if (valid(sName)) vr.font.name = sName;
          if (typeof sSize === 'number' && !isNaN(sSize) && sSize > 0) vr.font.size = sSize;
          cc.getRange().insertBookmark(`dn_${safeId}`);
        } else {
          cc.insertText(note.label, Word.InsertLocation.replace);
          cc.font.color = '#2f8570';
          cc.font.underline = Word.UnderlineType.dotted;
          cc.appearance = Word.ContentControlAppearance.hidden;
          const range = cc.getRange();
          range.font.color = '#2f8570';
          range.font.underline = Word.UnderlineType.dotted;
          // insertText inherits the content control's existing font, so toggling back to
          // Visible would keep a stale size baked in from the previous (discreet) render.
          // Force the surrounding size/family so the visible label matches its context.
          if (typeof sSize === 'number' && !isNaN(sSize) && sSize > 0) range.font.size = sSize;
          if (valid(sName)) range.font.name = sName;
        }
        cc.tag = freshTag;
        await context.sync();
      });
      // The insertHtml/replace moves the selection into the note, firing a stray
      // DocumentSelectionChanged. Consume it (same guard as the modal close) so changing
      // a note's visibility from the Library doesn't pop the note modal open.
      suppressReopenRef.current = note.id;
      setStatusMsg({ type: 'success', text: `Note set to ${mode === 'discreet' ? 'Discreet' : 'Visible'}.` });
      setTimeout(() => setStatusMsg(null), 2000);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: `Failed to update note: ${err.message}` });
    } finally {
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  const handleEditClick = (note: MarginNote) => {
    setEditingNoteId(note.id); setLabel(note.label); setContent(note.content);
    setIsGlobal(note.isGlobal || false); setAttachmentUrl(note.attachmentUrl || "");
    setVisibility(note.visibility || 'private');
    setAttachmentName(note.attachmentName || ""); setAttachmentType(note.attachmentType || "");
    setAttachmentPath(note.attachmentPath || ""); setExcelShareUrl(note.excelShareUrl || "");
    setAppendixMode(note.appendixMode || 'snapshot');
    setCurrentTemplateId(note.templateId || null);
    // Open in the table editor for catalog templates OR any note whose content is a
    // table (e.g. imported via YAML, which carries no templateId). Otherwise the
    // plain-text editor would flatten the table into run-together text.
    const hasTable = /<table[\s>]/i.test(note.content || "");
    if ((note.templateId && note.templateId !== "tpl-general-blank") || hasTable) {
      setTemplateWordHtml(note.content);
    } else {
      setTemplateWordHtml(null);
    }
    // Load per-note display override
    const override = docDisplaySettings.overrides[note.id];
    if (override) {
      setNoteDisplayOverride(override.mode);
      setNoteIconOverride(override.iconId);
    } else {
      setNoteDisplayOverride('inherit');
      setNoteIconOverride('');
    }
    setSelectedValue("new"); setStatusMsg(null);
  };


  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    // ── Freemium Gate ─────────────────────────────────────────────────────────
    const attachCheck = checkAttachment(userPlan);
    if (!attachCheck.allowed) { setUpgradePrompt(attachCheck.upgradeMessage!); return; }
    // ─────────────────────────────────────────────────────────────────────────
    setUploadingAttachment(true);
    try {
      const path = `attachments/${user.uid}/${Date.now()}_${file.name}`;
      const url = await uploadAttachment(file, path);
      setAttachmentUrl(url); setAttachmentName(file.name); setAttachmentType(file.type); setAttachmentPath(path);
      setStatusMsg({ type: "success", text: "File attached!" });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err) { console.error(err); }
    finally { setUploadingAttachment(false); }
  };
  /**
   * Removes a note's content control from the document.
   * keepText=false → removes the CC and its content (label + icon) entirely.
   * keepText=true  → removes the CC wrapper but leaves the label text as plain text (unlink).
   */
  const handleRemoveFromDocument = async (noteId: string, keepText = false) => {
    isInsertingRef.current = true;
    try {
      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load("items/tag");
        await context.sync();
        for (const cc of contentControls.items) {
          if (cc.tag && cc.tag.split("|")[0] === noteId) {
            if (keepText) {
              // For unlink: replace CC content with just the label text (strips the icon)
              // then delete the CC wrapper keeping the plain text
              const parts = cc.tag.split('|');
              const noteInLib = notesRef.current.find(n => n.id === noteId);
              const labelOnly = noteInLib ? noteInLib.label : (parts[0] || '');
              cc.insertText(labelOnly, Word.InsertLocation.replace);
            }
            // keepContent=true keeps text, keepContent=false removes everything
            cc.delete(keepText);
          }
        }
        await context.sync();
      });
      setStatusMsg({ type: 'success', text: keepText ? 'Note unlinked (label kept).' : 'Note removed from document.' });
      setTimeout(() => setStatusMsg(null), 3000);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: `Could not remove from document: ${err.message}` });
    } finally {
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  /** True if the note's content control is present in the current document. */
  const noteIsInDocument = async (noteId: string): Promise<boolean> => {
    try {
      return await Word.run(async (context) => {
        const ccs = context.document.contentControls;
        ccs.load("items/tag");
        await context.sync();
        return ccs.items.some(cc => cc.tag && cc.tag.split("|")[0] === noteId);
      });
    } catch {
      return false;
    }
  };

  /** First stage of delete: if the note is in the doc, ask how to handle the
   *  in-doc marker; otherwise delete straight away. */
  const beginDelete = async (noteId: string) => {
    setConfirmDeleteId(null);
    if (await noteIsInDocument(noteId)) {
      setDocRemovePromptId(noteId);
    } else {
      await handleDeleteClick(noteId, false);
    }
  };

  const handleDeleteClick = async (noteId: string, removeFromDoc = false) => {
    // Suppress the stray selection→reopen while we edit the document.
    isInsertingRef.current = true;
    try {
      await removeNote(noteId, graphToken);
      setDocRemovePromptId(null);

      // A deleted note must no longer be openable/readable from the document.
      // For each in-document content control of this note:
      //   removeFromDoc=true  → cc.delete(false): remove the marker AND its text.
      //   removeFromDoc=false → cc.delete(true):  keep the document text/table,
      //     just drop the interactive CC wrapper + tag so it's no longer clickable
      //     and the selection handler won't recognize it as a note.
      try {
        await Word.run(async (context) => {
          const contentControls = context.document.contentControls;
          contentControls.load("items/tag");
          await context.sync();
          for (const cc of contentControls.items) {
            if (cc.tag && cc.tag.split("|")[0] === noteId) {
              cc.delete(!removeFromDoc);
            }
          }
          await context.sync();
        });
      } catch (docErr) {
        console.error("Failed to update document on delete:", docErr);
      }

      // Clear the cached copy the modal could otherwise still load, and close the
      // modal if it's currently showing this note.
      try { localStorage.removeItem("marginpro_active_note_" + noteId); } catch (_) { /* ignore */ }
      if (activeDialogNoteRef.current === noteId) closeActiveNoteDialog();

      const updatedList = notes.filter(n => n.id !== noteId);
      setNotes(updatedList);
      setConfirmDeleteId(null);
      setStatusMsg({ type: "success", text: "Note deleted!" });
      setTimeout(() => setStatusMsg(null), 3000);
      if (hasAppendix) {
        await handleInsertAppendix(updatedList);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => { isInsertingRef.current = false; }, 400);
    }
  };

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedValue(data.value as any); setStatusMsg(null); setSearchQuery("");
  };

  const handleOpenDialog = (noteId: string) => {
    const foundNote = notes.find(n => n.id === noteId);
    if (foundNote) {
      try {
        localStorage.setItem("marginpro_active_note_" + noteId, JSON.stringify(foundNote));
      } catch (e) {
        console.error("Failed to write opened note to localStorage:", e);
      }
    }
    // If a modal is already open, switch it to this note IN PLACE via messageChild instead
    // of closing/reopening. Office allows only one dialog at a time and close is async, so
    // close-then-open races (and could fail to reopen). Reusing the open dialog avoids that.
    if (activeDialogRef.current && dialogActiveRef.current) {
      if (activeDialogNoteRef.current === noteId) return; // already showing this note
      try {
        activeDialogRef.current.messageChild(JSON.stringify({ type: "SWITCH_NOTE", noteId }));
        activeDialogNoteRef.current = noteId;
        return;
      } catch (e) {
        closeActiveNoteDialog(); // dialog is gone — fall through to open a fresh one
      }
    }
    // No modal open — open a fresh one.
    dialogActiveRef.current = true;
    activeDialogNoteRef.current = noteId;
    const url = new URL("dialog.html", window.location.href);
    url.searchParams.set("noteId", noteId);
    url.searchParams.set("v", Date.now().toString()); // Cache buster
    Office.context.ui.displayDialogAsync(url.toString(), { height: 60, width: 50, displayInIframe: true }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const dialog = asyncResult.value;
        activeDialogRef.current = dialog;
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          if (activeDialogRef.current !== dialog) return;
          dialogActiveRef.current = false;
          activeDialogRef.current = null;
          activeDialogNoteRef.current = null;
        });
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
          try {
            const data = JSON.parse(arg.message);
            if (data.type === "READY" && graphToken) {
              if (typeof dialog.messageChild === "function") {
                dialog.messageChild(JSON.stringify({ type: "TOKEN", token: graphToken }));
              } else {
                console.warn("dialog.messageChild is not supported in this version of Office.");
              }
            } else if (data.type === "NOTE_UPDATED" && data.noteId) {
              // User edited the note inside the modal — refresh the library list, and
              // if an appendix is present, regenerate it so it reflects the new content.
              const updatedNotes = notesRef.current.map(n => n.id === data.noteId ? { ...n, content: data.content } : n);
              setNotes(updatedNotes);
              if (hasAppendixRef.current) handleInsertAppendix(updatedNotes);
            } else if (data.type === "EDIT_IN_PANE" && data.noteId) {
              // Table notes hand off to the task-pane editor (preserves table layout).
              const nid = data.noteId;
              activeDialogRef.current = null;
              activeDialogNoteRef.current = null;
              dialogActiveRef.current = false;
              suppressReopenRef.current = nid;
              try { dialog.close(); } catch (e) {}
              const target = notesRef.current.find(n => n.id === nid);
              if (target) setTimeout(() => handleEditClick(target), 80);
            }
          } catch (e) {}
        });
      } else {
        dialogActiveRef.current = false;
        activeDialogRef.current = null;
        activeDialogNoteRef.current = null;
      }
    });
  };

  if (isPaywalled) {
    return renderPaywallUI();
  }

  return (
    <div className={menuStyles.container}>
      {/* Stripe plan picker overlay (personal Microsoft accounts) */}
      {showPlanPicker && renderPlanPicker()}

      {/* ── Trial countdown bar (slim; only during the trial) ──────────────── */}
      {trialInfo?.onTrial && !trialBarDismissed && (() => {
        const urgent = trialInfo.daysLeft <= 3;
        const accent = urgent ? '#c98a1e' : '#2f8570';
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            padding: '6px 12px', flexShrink: 0,
            background: urgent ? 'rgba(201,138,30,0.12)' : 'rgba(47,133,112,0.10)',
            borderBottom: `1px solid ${urgent ? 'rgba(201,138,30,0.4)' : 'rgba(47,133,112,0.25)'}`,
          }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: accent }}>
              ⏳ Free trial — {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'} left
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <button
                onClick={startSubscribe}
                style={{
                  background: 'linear-gradient(135deg, #2f8570, #1a5548)', color: '#fff',
                  border: 'none', fontSize: '11px', fontWeight: 600, padding: '4px 12px',
                  borderRadius: '6px', cursor: 'pointer',
                }}
              >Subscribe</button>
              <button
                onClick={() => setTrialBarDismissed(true)}
                aria-label="Dismiss"
                style={{ background: 'none', border: 'none', color: '#59766C', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: 0 }}
              >×</button>
            </div>
          </div>
        );
      })()}

      {/* ── Upgrade Prompt Banner ────────────────────────────────────────── */}
      {upgradePrompt && (
        <div style={{
          background: 'linear-gradient(135deg, #1a5548, #0f2d27)',
          borderBottom: '1px solid #2f8570',
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
            <p style={{ color: '#7ec0ac', fontSize: '12px', lineHeight: '1.4', margin: 0, flex: 1 }}>
              ⭐ {upgradePrompt}
            </p>
            <button
              onClick={() => setUpgradePrompt(null)}
              style={{ background: 'none', border: 'none', color: '#7ec0ac', cursor: 'pointer', fontSize: '16px', padding: '0', lineHeight: 1, flexShrink: 0 }}
              aria-label="Dismiss"
            >×</button>
          </div>
          <a
            href={isPersonalAccount ? undefined : MARKETPLACE_OFFER_URL}
            target={isPersonalAccount ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={isPersonalAccount ? (e) => { e.preventDefault(); setShowPlanPicker(true); } : undefined}
            style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #2f8570, #1a5548)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '600',
              padding: '5px 12px',
              borderRadius: '6px',
              textDecoration: 'none',
              alignSelf: 'flex-start',
              cursor: 'pointer',
            }}
          >
            Upgrade →
          </a>
        </div>
      )}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <div className={menuStyles.headerSticky}>
        {/* ── Logo Row ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <img src={`assets/Logomark-Final.png?v=${Date.now()}`} alt="Margin Logo" style={{ height: "34px", width: "auto", marginLeft: "4px" }} />
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!hasSyncToken && (
              <Button 
                appearance="primary" 
                size="small" 
                style={{ backgroundColor: "#0078d4", border: "none", height: "24px", fontSize: "11px" }}
                onClick={async () => {
                   setStatusMsg({ type: "warning", text: "Linking OneDrive..." });
                   try {
                     // Already signed in — only a Graph token is needed. Personal (MSA)
                     // accounts skip loginWithSSO (which would open a second sign-in
                     // dialog → the "enter email twice" bug) and go straight to the
                     // single Graph dialog. Work/school try silent OBO first.
                     let resolvedToken: string | null = null;
                     if (!isPersonalAccount) {
                       try {
                         const result = await loginWithSSO();
                         resolvedToken = result.graphToken;
                       } catch (_) { /* fall through to dialog */ }
                     }

                     if (!resolvedToken) {
                       resolvedToken = await getGraphTokenViaDialog();
                     }

                     if (resolvedToken) {
                       localStorage.setItem('marginpro_graph_token', resolvedToken);
                       setGraphToken(resolvedToken);
                       setHasSyncToken(true);
                       setStatusMsg({ type: "success", text: "OneDrive Connected! ✅" });
                       setTimeout(() => setStatusMsg(null), 5000);
                       const currentNotes = notes.filter((n: any) => n.documentId === curDocId && !n.isGlobal);
                       for (const n of currentNotes) {
                         await syncNoteToVault(n, resolvedToken);
                       }
                     }
                   } catch (err: any) {
                     console.error("Link failed:", err);
                     setStatusMsg({ type: "error", text: "Failed to link OneDrive: " + err.message });
                   }
                }}
              >
                Enable OneDrive
              </Button>
            )}
            {hasSyncToken && (
               <Caption1 style={{ color: "#59766C", fontWeight: "bold", fontSize: "10px" }}>
                 Cloud Sync Active ✅
               </Caption1>
            )}
            <Tooltip content={`Logged in as: ${user.email || user.displayName || 'Microsoft Account'}`} relationship="description">
              <Button
                className={menuStyles.signOutBtn}
                appearance="subtle"
                size="small"
                onClick={() => setShowSignOutDialog(true)}
              >
                Sign Out
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* ── Plan Status Strip ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '4px 2px 2px 2px',
          padding: '4px 10px',
          borderRadius: '6px',
          background: userPlan === 'Free'
            ? tokens.colorNeutralBackground3
            : 'linear-gradient(90deg, #e6f4f0 0%, #f0faf7 100%)',
          border: userPlan === 'Free'
            ? `1px solid ${tokens.colorNeutralStroke2}`
            : '1px solid #a8d8cc',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              fontSize: '9px',
              fontWeight: '700',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: '20px',
              background: userPlan === 'Free' ? tokens.colorNeutralBackground5 : '#2f8570',
              color: userPlan === 'Free' ? tokens.colorNeutralForeground3 : '#ffffff',
            }}>
              {userPlan === 'Free' ? 'Unlicensed' : userPlan === 'Personal' ? 'Personal' : userPlan === 'Professional' ? '⭐ Professional' : '🏢 Enterprise'}
            </span>
            {trialInfo?.onTrial ? (
              <span style={{ fontSize: '10px', color: tokens.colorNeutralForeground3 }}>
                Trial · {trialInfo.daysLeft}d left
              </span>
            ) : userPlan === 'Free' ? (
              <span style={{ fontSize: '10px', color: tokens.colorNeutralForeground3 }}>
                Trial ended
              </span>
            ) : null}
          </div>
          <a
            onClick={(e) => {
              e.preventDefault();
              if (trialInfo?.onTrial || userPlan === 'Free') startSubscribe();
              else if (isPersonalAccount) openStripePortal();
              else window.open(MARKETPLACE_OFFER_URL, "_blank");
            }}
            style={{
              fontSize: '10px',
              fontWeight: '600',
              color: (trialInfo?.onTrial || userPlan === 'Free') ? '#2f8570' : '#59766C',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {trialInfo?.onTrial ? 'Subscribe →' : userPlan === 'Free' ? 'Upgrade →' : 'Manage'}
          </a>
        </div>

        <div style={{ display: 'flex', gap: '12px', padding: '0 8px 8px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
          <a
            href="https://marginproapp.com/guide"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '10px', color: tokens.colorNeutralForeground3, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
            onMouseOver={(e) => (e.currentTarget.style.color = '#2f8570')}
            onMouseOut={(e) => (e.currentTarget.style.color = tokens.colorNeutralForeground3)}
          >
            📖 User Guide
          </a>
          <a
            href="https://marginproapp.com/security"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '10px', color: tokens.colorNeutralForeground3, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
            onMouseOver={(e) => (e.currentTarget.style.color = '#2f8570')}
            onMouseOut={(e) => (e.currentTarget.style.color = tokens.colorNeutralForeground3)}
          >
            🛡️ Security & Trust
          </a>
        </div>

        <div style={{ overflowX: "hidden" }}>
          <TabList selectedValue={selectedValue} onTabSelect={onTabSelect} style={{ flexWrap: "wrap" }}>
            <Tab value="new">New Note</Tab>
            <Tab value="library">Library</Tab>
            <Tab value="marketplace" icon={<AppsListDetail24Regular style={{ width: 14, height: 14 }} />}>Store</Tab>
            <Tab value="settings">Settings</Tab>
            {role === 'admin' && <Tab value="management">Management</Tab>}
          </TabList>
        </div>

        {statusMsg && (
          <div style={{ padding: "8px 0 4px 0", maxWidth: "100%", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
            <MessageBar 
              intent={statusMsg.type as any}
              layout="multiline"
            >
              <MessageBarBody style={{ wordBreak: "break-word" }}>{statusMsg.text}</MessageBarBody>
              {(statusMsg.text.includes("waiting for Login") || statusMsg.text.includes("Auth Required") || statusMsg.text.includes("(401)") || statusMsg.text.includes("Unauthorized") || statusMsg.text.includes("Reconnect")) && (
                <div style={{ marginTop: '8px' }}>
                  <Button 
                    size="small" 
                    appearance="primary" 
                    onClick={handleReconnect}
                    style={{ backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 }}
                  >
                    Reconnect
                  </Button>
                </div>
              )}
            </MessageBar>
          </div>
        )}

      </div>

      <div className={menuStyles.scrollBody}>

      {checkingWhitelist && <Spinner label="Verifying Pilot enrollment..." style={{ marginTop: '20px' }} />}

      {!checkingWhitelist && !isWhitelisted && role !== 'admin' && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <img src="assets/Logomark-Final.png" alt="Logo" style={{ height: '60px', marginBottom: '20px' }} />
          <Subtitle1 block style={{ marginBottom: '12px', color: tokens.colorPaletteRedForeground1 }}>Access Restricted</Subtitle1>
          <Body1 block style={{ marginBottom: '24px' }}>
            Your department is not yet enrolled in the MarginPro pilot. 
            Please contact your Portfolio Manager for access.
          </Body1>
          <Tooltip content={`Logged in as: ${user.email || user.displayName || 'Microsoft Account'}`} relationship="description">
            <Button appearance="subtle" onClick={() => setShowSignOutDialog(true)} className={menuStyles.signOutBtn}>Sign Out</Button>
          </Tooltip>
        </div>
      )}

      {!checkingWhitelist && (isWhitelisted || role === 'admin') && selectedValue === "marketplace" && (
        <Marketplace
          user={user}
          userPlan={userPlan}
          onUseTemplate={(templateId, title, editorContent, wordHtml, usesTable) => {
            setLabel(title);
            setCurrentTemplateId(templateId);
            if (usesTable) {
              setTemplateWordHtml(wordHtml);
              setContent(wordHtml);
            } else {
              setTemplateWordHtml(null);
              setContent(editorContent);
            }
            setSelectedValue("new");
            setStatusMsg({ type: "info", text: `"${title}" template loaded. Edit the content and save your note.` });
          }}
          onRefreshPlan={() => {
            if (isOwnerDev) {
              setUserPlan('Professional');
            } else {
              getUserPlan(user.uid).then(setUserPlan).catch(() => setUserPlan('Free'));
            }
          }}
        />
      )}

      {!checkingWhitelist && (isWhitelisted || role === 'admin') && selectedValue === "new" && (
        isGuestMode ? (
          renderGuestModeUI()
        ) : (
          <>
          {editingNoteId && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <Subtitle1>Editing Note...</Subtitle1>
              <Button appearance="subtle" onClick={() => {
                setEditingNoteId(null); setLabel(""); setContent(""); setAttachmentUrl(""); setIsGlobal(false); setVisibility("private"); setTemplateWordHtml(null); setCurrentTemplateId(null);
                setAttachmentName(""); setAttachmentType(""); setAttachmentPath(""); setExcelShareUrl(""); setAppendixMode('snapshot');
                setExcelInputMode(false); setExcelUrlInput(""); setExcelWorksheets([]); setExcelDriveId(""); setExcelSheetSelection(""); setExcelRangeSelection("");
      setPptInputMode(false); setPptUrlInput("");
                setNoteDisplayOverride('inherit'); setNoteIconOverride('');
              }}>Cancel</Button>
            </div>
          )}
          <Field label="Note Label">
            <Input value={label} maxLength={50} onChange={(_, data) => setLabel(data.value)} placeholder="e.g., Background detail" />
            <Caption1 className={menuStyles.charCounter}>{label.length}/50 characters</Caption1>
          </Field>
          <Field label="Content">
            {attachmentType === 'powerpoint-link' ? (
              <div style={{ padding: "16px", backgroundColor: "#f9f9f9", borderRadius: "8px", border: "1px solid #e0e0e0", textAlign: "center", marginBottom: "12px" }}>
                <SlideText24Regular style={{ fontSize: "28px", color: "#2f8570", marginBottom: "8px" }} />
                <Subtitle1 block style={{ marginBottom: "8px", fontWeight: "600" }}>Linked PowerPoint</Subtitle1>
                <Body1 block style={{ color: tokens.colorNeutralForeground3, marginBottom: "16px", fontSize: "12px" }}>
                  This note links to a PowerPoint deck. Open it in PowerPoint to view or edit the full presentation.
                </Body1>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    let u = "";
                    try { u = JSON.parse(content)?.webUrl || ""; } catch { /* ignore */ }
                    if (isSafeWebUrl(u)) window.open(u, "_blank", "noopener,noreferrer");
                  }}
                >
                  Open in PowerPoint
                </Button>
              </div>
            ) : attachmentType === 'excel-live-link' ? (
              <div style={{ padding: "16px", backgroundColor: "#f9f9f9", borderRadius: "8px", border: "1px solid #e0e0e0", textAlign: "center", marginBottom: "12px" }}>
                <TableStackLeft24Regular style={{ fontSize: "28px", color: "#2f8570", marginBottom: "8px" }} />
                <Subtitle1 block style={{ marginBottom: "8px", fontWeight: "600" }}>Live Excel Data</Subtitle1>
                <Body1 block style={{ color: tokens.colorNeutralForeground3, marginBottom: "16px", fontSize: "12px" }}>
                  This note automatically syncs data directly from your Excel workbook. To edit the values, update your source Excel file.
                </Body1>
                <Tooltip content={excelShareUrl || "https://www.office.com/launch/excel"} relationship="description">
                  <Button 
                    appearance="secondary" 
                    onClick={() => {
                       const u = excelShareUrl && isSafeWebUrl(excelShareUrl) ? excelShareUrl : "https://www.office.com/launch/excel";
                       window.open(u, "_blank", "noopener,noreferrer");
                    }}
                  >
                    Open in OneDrive
                  </Button>
                </Tooltip>
              </div>
            ) : templateWordHtml ? (
              // Template mode: show the full HTML table in a live contentEditable editor
              <>
                <TemplateEditor
                  initialHtml={templateWordHtml}
                  onChange={(html) => setContent(html)}
                />
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ marginTop: '4px', color: tokens.colorNeutralForeground3, fontSize: '11px' }}
                  onClick={() => {
                    setTemplateWordHtml(null);
                    setCurrentTemplateId(null);
                    setContent("");
                  }}
                >
                  ✕ Clear template & use text editor
                </Button>
              </>
            ) : (
              // Regular mode: ReactQuill rich text editor
              <div className={menuStyles.editorWrapper}>
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  placeholder="Capture your thoughts here..."
                />
              </div>
            )}
            <input
              ref={templateYamlInputRef}
              type="file"
              accept=".yaml,.yml,text/yaml"
              style={{ display: "none" }}
              onChange={onTemplateYamlFile}
            />
            <input
              ref={noteMdInputRef}
              type="file"
              accept=".md,.markdown,text/markdown"
              style={{ display: "none" }}
              onChange={onNoteMdFile}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
              <Tooltip
                relationship="label"
                content={
                  editingIsTable()
                    ? "Exports this table as a shareable YAML (.yaml) file."
                    : "Exports this note as a Markdown (.md) file. Tables export as YAML."
                }
              >
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ color: tokens.colorNeutralForeground3, fontSize: '11px' }}
                  onClick={() => (editingIsTable() ? exportTemplateYaml() : exportNoteMarkdown())}
                >
                  ⤓ Export {editingIsTable() ? "(.yaml)" : "(.md)"}
                </Button>
              </Tooltip>
              <Tooltip relationship="label" content="Load a note from a Markdown (.md) file">
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ color: tokens.colorNeutralForeground3, fontSize: '11px' }}
                  onClick={() => noteMdInputRef.current?.click()}
                >
                  ⤒ Import note (.md)
                </Button>
              </Tooltip>
              <Tooltip relationship="label" content="Load a table template from a YAML (.yaml) file">
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ color: tokens.colorNeutralForeground3, fontSize: '11px' }}
                  onClick={() => templateYamlInputRef.current?.click()}
                >
                  ⤒ Import template (.yaml)
                </Button>
              </Tooltip>
            </div>
          </Field>
          <Field label="Attachments">
            <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '6px' }}>
              Only upload or link content you own or have the rights to share. See our{' '}
              <a href="https://marginproapp.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2f8570' }}>Terms</a>.
            </Caption1>
            {uploadingAttachment ? (
              <div className={menuStyles.attachmentZone}><Spinner label="Uploading..." /></div>
            ) : attachmentType === 'excel-live-link' ? (
              <div className={menuStyles.attachmentZone} style={{ cursor: "default", height: "auto", padding: "12px" }}>
                <TableStackLeft24Regular />
                <Body1><b>{attachmentName}</b>!{attachmentPath}</Body1>
                <Caption1>Linked to Drive ID: {attachmentUrl.substring(0, 10)}...</Caption1>
                <Button appearance="subtle" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachmentUrl(""); setAttachmentPath(""); setAttachmentType(""); setAttachmentName(""); setExcelShareUrl(""); }}>Remove Link</Button>
              </div>
            ) : attachmentType === 'powerpoint-link' ? (
              <div className={menuStyles.attachmentZone} style={{ cursor: "default", height: "auto", padding: "12px" }}>
                <SlideText24Regular />
                <Body1><b>{attachmentName}</b></Body1>
                <Caption1>Linked PowerPoint</Caption1>
                <Button appearance="subtle" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachmentUrl(""); setAttachmentType(""); setAttachmentName(""); setContent(""); }}>Remove Link</Button>
              </div>
            ) : attachmentUrl ? (
              <div className={menuStyles.attachmentZone} style={{ cursor: "default" }}>
                <Body1><b>{attachmentName}</b> attached</Body1>
                <Button appearance="subtle" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachmentUrl(""); setAttachmentPath(""); }}>Remove</Button>
              </div>
            ) : excelInputMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Subtitle1>Link Excel Range</Subtitle1>
                  {excelLoading && <Spinner size="extra-tiny" />}
                </div>

                {!excelDriveId ? (
                  !graphToken ? (
                    // Gate: linking live Excel needs an OneDrive connection. Prompt to
                    // connect first instead of letting the paste-and-fail happen.
                    <>
                      <Body1>Linking live Excel data needs an active OneDrive connection. Connect OneDrive first, then paste your Excel link.</Body1>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                        <Button appearance="subtle" onClick={() => { setExcelInputMode(false); setExcelUrlInput(""); }}>Cancel</Button>
                        <Button appearance="primary" style={{ backgroundColor: '#2f8570' }} onClick={handleReconnect}>Connect OneDrive</Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Body1>Paste a sharing link to an Excel file in your OneDrive/SharePoint:</Body1>
                      <Input
                        placeholder="https://...sharepoint.com/:x:/g/..."
                        value={excelUrlInput}
                        onChange={(_, data) => setExcelUrlInput(data.value)}
                      />
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                        <Button appearance="subtle" onClick={() => { setExcelInputMode(false); setExcelUrlInput(""); }}>Cancel</Button>
                        <Button appearance="primary" disabled={!excelUrlInput} onClick={() => executeExcelLoad(graphToken!, excelUrlInput)}>Load File</Button>
                      </div>
                    </>
                  )
                ) : (
                  <>
                    <Field label="Select Worksheet">
                      <Dropdown 
                        placeholder="Select a sheet" 
                        value={excelSheetSelection}
                        onOptionSelect={(_, data) => setExcelSheetSelection(data.optionValue as string)}
                      >
                        {excelWorksheets.map(ws => (
                          <Option key={ws.id} value={ws.name}>{ws.name}</Option>
                        ))}
                      </Dropdown>
                    </Field>
                    
                    <Field label="Range or Table (Optional)">
                      <Input 
                        placeholder="Leave blank for entire used range, or specify e.g. A1:D10" 
                        value={excelRangeSelection}
                        onChange={(_, data) => setExcelRangeSelection(data.value)} 
                      />
                    </Field>

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                      <Button appearance="subtle" onClick={() => { setExcelDriveId(""); setExcelWorksheets([]); }}>Back</Button>
                      <Button appearance="primary" disabled={!excelSheetSelection} onClick={() => {
                        if (excelDriveId && excelSheetSelection) {
                          setAttachmentUrl(excelDriveId);
                          setAttachmentName(excelSheetSelection);
                          setAttachmentPath(excelRangeSelection ? excelRangeSelection : 'auto');
                          setAttachmentType('excel-live-link');
                          setExcelInputMode(false);
                        }
                      }}>Link Data</Button>
                    </div>
                  </>
                )}
              </div>
            ) : pptInputMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Subtitle1>Link PowerPoint</Subtitle1>
                  {pptLoading && <Spinner size="extra-tiny" />}
                </div>
                {!graphToken ? (
                  <>
                    <Body1>Linking a PowerPoint needs an active OneDrive connection. Connect OneDrive first, then paste your link.</Body1>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                      <Button appearance="subtle" onClick={() => { setPptInputMode(false); setPptUrlInput(""); }}>Cancel</Button>
                      <Button appearance="primary" style={{ backgroundColor: '#2f8570' }} onClick={handleReconnect}>Connect OneDrive</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Body1>Paste a sharing link to a PowerPoint file in your OneDrive/SharePoint:</Body1>
                    <Input
                      placeholder="https://...sharepoint.com/:p:/g/..."
                      value={pptUrlInput}
                      onChange={(_, data) => setPptUrlInput(data.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                      <Button appearance="subtle" onClick={() => { setPptInputMode(false); setPptUrlInput(""); }}>Cancel</Button>
                      <Button appearance="primary" disabled={!pptUrlInput || pptLoading} onClick={() => executePptLoad(graphToken!, pptUrlInput)}>Load File</Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <label
                  className={menuStyles.attachmentZone} 
                  htmlFor="note-file-upload"
                  onMouseDown={(e) => e.stopPropagation()} 
                  onClick={(e) => e.stopPropagation()}
                >
                  <input 
                    id="note-file-upload"
                    type="file" 
                    accept="image/*, application/pdf" 
                    className={menuStyles.fileInput}
                    onChange={handleFileUpload} 
                  />
                  <Attach24Regular />
                  <span>Attach file</span>
                </label>
                <div
                  className={menuStyles.attachmentZone}
                  onClick={() => setExcelInputMode(true)}
                >
                  <TableStackLeft24Regular />
                  <span>Link Excel</span>
                </div>
                <div
                  className={menuStyles.attachmentZone}
                  onClick={() => setPptInputMode(true)}
                >
                  <SlideText24Regular />
                  <span>Link PowerPoint</span>
                </div>
              </div>
            )}
          </Field>

          {(attachmentType === 'powerpoint-link' || attachmentType === 'excel-live-link') && (
            <Field label="In the Appendix, show this as">
              <Dropdown
                value={appendixMode === 'link' ? 'Share link to the original' : 'Snapshot (static preview)'}
                selectedOptions={[appendixMode === 'link' ? 'link' : 'snapshot']}
                onOptionSelect={(_, data) => {
                  setAppendixMode((data.optionValue as 'snapshot' | 'link') || 'snapshot');
                }}
              >
                <Option value="snapshot" text="Snapshot (static preview)">
                  Snapshot — a static preview ({attachmentType === 'excel-live-link' ? 'the captured cells' : 'the cover slide'})
                </Option>
                <Option value="link" text="Share link to the original">
                  Share link — a link to the original file (follows its OneDrive sharing permissions)
                </Option>
              </Dropdown>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                Controls only how this note appears in a generated appendix. It does not change the note itself.
              </Caption1>
            </Field>
          )}

          <Field label="Note Type">
            <Dropdown
              placeholder="Select Workspace..."
              value={
                visibility === 'private' ? "Personal Workspace" :
                visibility === 'tenant_read' ? "Team Workspace (Read-only)" :
                "Team Workspace (Collaborative)"
              }
              onOptionSelect={(_, data) => setVisibility(data.selectedOptions[0] as any)}
            >
              <Option value="private">Personal Workspace</Option>
              <Option value="tenant_read">Team Workspace (Read-only)</Option>
              <Option value="tenant_edit">Team Workspace (Collaborative)</Option>
            </Dropdown>
          </Field>
          
          <Field><Switch label="Make Note Global" checked={isGlobal} onChange={(_, data) => setIsGlobal(data.checked)} /></Field>

          {/* ── Per-note display override ── */}
          <Field label="Note Display Style">
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {(['inherit', 'visible', 'discreet'] as const).map(m => (
                <Button
                  key={m}
                  size="small"
                  appearance={noteDisplayOverride === m ? 'primary' : 'outline'}
                  style={noteDisplayOverride === m ? { backgroundColor: '#2f8570' } : {}}
                  onClick={() => setNoteDisplayOverride(m)}
                >
                  {m === 'inherit' ? `Default (${docDisplaySettings.mode})` : m === 'visible' ? '👁 Visible' : '◆ Discreet'}
                </Button>
              ))}
            </div>
            {noteDisplayOverride === 'discreet' && (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                {CLASSIC_ICONS.map(icon => (
                  <Tooltip key={icon.id} content={icon.label} relationship="label">
                    <Button
                      size="small"
                      appearance={(noteIconOverride || docDisplaySettings.iconId) === icon.id ? 'primary' : 'outline'}
                      style={{
                        minWidth: '30px', padding: '2px 6px',
                        fontSize: icon.isLogo ? '9px' : '13px',
                        fontWeight: icon.isLogo ? 900 : 400,
                        ...((noteIconOverride || docDisplaySettings.iconId) === icon.id ? { backgroundColor: '#2f8570' } : {}),
                      }}
                      onClick={() => setNoteIconOverride(icon.id)}
                    >
                      {icon.char}
                    </Button>
                  </Tooltip>
                ))}
              </div>
            )}
          </Field>

          <div className={menuStyles.buttonGroup}>
            <Button disabled={!label.trim() || excelInputMode || pptInputMode} onClick={handleSaveOnly} icon={<Save24Regular />}>Save Only</Button>
            <Button disabled={!label.trim() || excelInputMode || pptInputMode || (hasAppendix && !editingNoteId)} appearance="primary" onClick={handleCreateAndInsert} icon={<DocumentArrowRight24Regular />} >Save & Insert</Button>
          </div>
          {hasAppendix && !editingNoteId && (
            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '6px', lineHeight: 1.4 }}>
              The appendix is open, so notes can't be inserted into the document. Use <strong>Save Only</strong> to add to your Library, or remove the appendix to insert notes and then regenerate it.
            </div>
          )}
        </>
        )
      )}

      {selectedValue === "library" && (
        isGuestMode ? renderGuestModeUI() : (() => {
        const primaryNotes = libraryTab === "local"
          ? notes.filter(n => n.documentId === curDocId && !n.isGlobal)
          : notes.filter(n => n.isGlobal === true);

        const displayedNotes = primaryNotes.filter(n => {
          if (!searchQuery) return true;
          const term = searchQuery.toLowerCase();
          return n.label.toLowerCase().includes(term) || n.content.toLowerCase().includes(term);
        });

        return (
          <div className={menuStyles.cardList}>
            {libraryTab === "local" && primaryNotes.length > 0 && (
              hasAppendix ? (
                <Card appearance="filled-alternative" style={{ marginBottom: "16px", backgroundColor: "#fffbeb", border: "1px solid #fcd34d" }}>
                  <CardHeader 
                    header={<Subtitle2 style={{ color: "#b45309" }}>Appendix Active</Subtitle2>}
                    description="Notes are linked for PDF export. To edit or insert notes, return to edit mode."
                    action={
                      <Button 
                        appearance="outline" 
                        size="small" 
                        style={{ color: "#b45309", borderColor: "#f59e0b" }}
                        onClick={() => handleRemoveAppendix()}
                      >
                        Return to Edit Mode
                      </Button>
                    }
                  />
                </Card>
              ) : (
                <Card appearance="filled-alternative" style={{ marginBottom: "16px", backgroundColor: "#f0faf7", border: "1px solid #a8d8cc" }}>
                  <CardHeader 
                    header={<Subtitle2 style={{ color: "#2f8570" }}>{hadAppendix ? "Return to Appendix Mode" : "Finalize for PDF Export"}</Subtitle2>}
                    description={hadAppendix ? "Reactivate the live-linked appendix glossary at the end of your document." : "Add a research glossary of all notes to the end of your document."}
                    action={
                      <Button 
                        appearance="primary" 
                        size="small" 
                        style={{ backgroundColor: "#2f8570" }}
                        onClick={() => handleInsertAppendix()}
                      >
                        {hadAppendix ? "Reactivate Appendix" : "Insert Appendix"}
                      </Button>
                    }
                  />
                </Card>
              )
            )}
            <TabList size="small" selectedValue={libraryTab} onTabSelect={(_, data) => setLibraryTab(data.value as "local" | "global")}>
              <Tab value="local">Document Notes</Tab><Tab value="global">Global Notes</Tab>
            </TabList>
            <Divider style={{ marginTop: "4px", marginBottom: "8px" }} />
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <Input 
                value={searchQuery} 
                onChange={(_, data) => setSearchQuery(data.value)} 
                placeholder="Search..." 
                style={{ flexGrow: 1 }} 
              />
            </div>
            {isOwnerDev && (
              <div style={{ marginBottom: "12px" }}>
                <Switch 
                  label="Show notes from all environments" 
                  checked={showAllEnvironments} 
                  onChange={(_, data) => setShowAllEnvironments(data.checked)} 
                  labelPosition="after"
                />
              </div>
            )}
            {loadingNotes ? (<Spinner label="Loading..." />) : primaryNotes.length === 0 ? (<Body1>No notes found.</Body1>) : (
              displayedNotes.map(note => {
                const isLocked = lockedNotes[note.id] === true;
                return (
                  <a href="javascript:void(0)" style={{ textDecoration: 'none', color: 'inherit', display: 'block', cursor: isLocked ? 'not-allowed' : 'pointer' }} className="note-card-hand">
                    <Card 
                      key={note.id} 
                      appearance={isLocked ? "filled-alternative" : "outline"} 
                      className={menuStyles.noteCard} 
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.9 : 1, border: isLocked ? '1px dashed #d97706' : undefined, backgroundColor: isLocked ? '#fffbeb' : undefined }}
                      role="button" 
                      tabIndex={0}
                      onClick={() => {
                        if (isLocked) {
                          setStatusMsg({ type: "warning", text: `🔒 "${note.label}" is a Premium template note. Upgrade or buy this template in the Marketplace to unlock.` });
                          setTimeout(() => setStatusMsg(null), 5000);
                          return;
                        }
                        handleOpenDialog(note.id);
                      }}
                      onKeyDown={(e) => { 
                        if (e.key === 'Enter' || e.key === ' ') {
                          if (isLocked) {
                            setStatusMsg({ type: "warning", text: `🔒 "${note.label}" is a Premium template note. Upgrade or buy this template in the Marketplace to unlock.` });
                            setTimeout(() => setStatusMsg(null), 5000);
                            return;
                          }
                          handleOpenDialog(note.id); 
                        }
                      }}
                    >
                    <CardHeader 
                      header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isLocked && <LockClosed20Regular style={{ color: '#d97706' }} />}
                          <Subtitle1 style={{ textDecoration: isLocked ? 'line-through' : 'none', color: isLocked ? '#9a3412' : 'inherit' }}>{note.label}</Subtitle1>
                          {note.visibility !== 'private' && <span className={menuStyles.badge}>Team</span>}
                          {isLocked && <span className={menuStyles.badge} style={{ backgroundColor: '#fef3c7', color: '#b45309', borderColor: '#fcd34d' }}>Locked</span>}
                        </div>
                      } 
                      description={isLocked ? "Professional Trial Expired" : new Date(note.createdAt).toLocaleString()} 
                    />
                    
                    {isLocked ? (
                      <div style={{ padding: '8px 12px', fontSize: '12px', color: '#78350f', backgroundColor: '#fffbeb', borderRadius: '4px', margin: '4px 0' }}>
                        🔒 This premium template note was saved during your trial. Upgrade to Professional or buy the template à la carte to view, edit, or insert this note.
                      </div>
                    ) : (
                      <>
                        {note.attachmentType === 'excel-live-link' && (
                          <ExcelLiveNote 
                            noteId={note.id}
                            driveItemId={note.attachmentUrl || ""}
                            worksheetName={note.attachmentName || ""}
                            rangeAddress={note.attachmentPath || ""}
                            initialData={note.content}
                            graphToken={graphToken}
                            noteLabel={note.label}
                            webUrl={note.excelShareUrl || ""}
                          />
                        )}

                        {note.attachmentType === 'powerpoint-link' && (
                          <PowerPointLinkNote
                            noteId={note.id}
                            driveItemId={note.attachmentUrl || ""}
                            initialData={note.content}
                            graphToken={graphToken}
                            noteLabel={note.label}
                          />
                        )}

                        {note.attachmentUrl && note.attachmentType !== 'excel-live-link' && note.attachmentType !== 'powerpoint-link' && (note.attachmentType?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(note.attachmentName || '')) && (
                          <div style={{ margin: "0 -12px", height: "100px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0" }}>
                            <img src={note.attachmentUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                        )}
                      </>
                    )}
                    
                    {isLocked ? (
                      <CardFooter onClick={(e) => e.stopPropagation()} style={{ flexWrap: 'wrap', rowGap: '4px', justifyContent: 'flex-end' }}>
                        <Button appearance="primary" style={{ backgroundColor: '#2f8570' }} onClick={() => setSelectedValue("marketplace")}>
                          Go to Marketplace
                        </Button>
                      </CardFooter>
                    ) : docRemovePromptId === note.id ? (
                      <CardFooter style={{ justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', rowGap: '4px' }} onClick={(e) => e.stopPropagation()}>
                        <Body1 style={{ color: tokens.colorPaletteRedForeground1, fontSize: '12px' }}>This note is in your document — remove it from the doc too?</Body1>
                        <Button appearance="primary" onClick={() => handleDeleteClick(note.id, true)} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}>Remove it too</Button>
                        <Button appearance="subtle" onClick={() => handleDeleteClick(note.id, false)}>Keep doc text</Button>
                        <Button appearance="subtle" onClick={() => setDocRemovePromptId(null)}>Cancel</Button>
                      </CardFooter>
                    ) : confirmDeleteId === note.id ? (
                      <CardFooter style={{ justifyContent: 'flex-end', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>Delete?</Body1>
                        <Button appearance="primary" onClick={() => beginDelete(note.id)} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}>Yes</Button>
                        <Button appearance="subtle" onClick={() => setConfirmDeleteId(null)}>No</Button>
                      </CardFooter>
                    ) : (
                      <CardFooter onClick={(e) => e.stopPropagation()} style={{ flexWrap: 'wrap', rowGap: '4px' }}>
                        <Button onClick={() => handleOpenDialog(note.id)}>View</Button>
                        <Button appearance="primary" onClick={() => insertWordNote(note)}>Insert</Button>
                        <Tooltip content="Paste full note content (tables, text, images) into the document at your cursor" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<ClipboardPaste24Regular />}
                            onClick={() => insertNoteContent(note)}
                            style={{ color: '#59766C' }}
                          />
                        </Tooltip>
                        {note.uid === user.uid && (
                          <>
                            <Tooltip
                              content={`Display: ${noteEffectiveMode(note.id) === 'discreet' ? 'Discreet' : 'Visible'} — click to switch this note`}
                              relationship="label"
                            >
                              <Button
                                appearance="subtle"
                                style={{ fontSize: '14px', minWidth: 'unset', padding: '4px 7px', color: '#2f8570' }}
                                onClick={() => setNoteDisplayMode(note, noteEffectiveMode(note.id) === 'discreet' ? 'visible' : 'discreet')}
                              >
                                {noteEffectiveMode(note.id) === 'discreet' ? '◆' : '👁'}
                              </Button>
                            </Tooltip>
                            <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => handleEditClick(note)} />
                            <Button appearance="subtle" icon={<DocumentArrowRight24Regular />} onClick={async () => {
                              const res = await syncNoteToVault(note, graphToken);
                              setStatusMsg({ type: res === "Success" ? "success" : "warning", text: `Cloud Sync: ${res}` });
                              setTimeout(() => setStatusMsg(null), 3000);
                            }} />
                            <Tooltip content="Remove from document — deletes label text and icon" relationship="label">
                              <Button appearance="subtle" icon={<DocumentDismiss24Regular />} onClick={() => handleRemoveFromDocument(note.id, false)} />
                            </Tooltip>
                            <Tooltip content="Unlink from document — keeps label text as plain text" relationship="label">
                              <Button appearance="subtle" style={{ fontSize: '11px', minWidth: 'unset', padding: '4px 6px' }} onClick={() => handleRemoveFromDocument(note.id, true)}>↩</Button>
                            </Tooltip>
                            <Button appearance="subtle" icon={<Delete24Regular />} onClick={() => setConfirmDeleteId(note.id)} />
                          </>
                        )}
                      </CardFooter>
                    )}
                  </Card>
                </a>
                );
              })
            )}
          </div>
        );
      })()
      )}

      {!checkingWhitelist && (isWhitelisted || role === 'admin') && selectedValue === "management" && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Card appearance="outline">
            <CardHeader header={<Subtitle1>Department Directory (SSO)</Subtitle1>} description={`Tenant ID: ${tenantId}`} />
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                  <th style={{ padding: '4px' }}>Member</th>
                  <th style={{ padding: '4px' }}>Role</th>
                </tr>
              </thead>
              <tbody>
                {adminUserList.map(u => (
                  <tr key={u.uid} style={{ borderTop: `1px solid ${tokens.colorNeutralStroke1}`, fontSize: '13px' }}>
                    <td style={{ padding: '8px 4px' }}>
                      <Body1 block>{u.displayName || "Unknown"}</Body1>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{u.email}</Caption1>
                    </td>
                    <td style={{ padding: '8px 4px' }}>
                      <Dropdown 
                        size="small" 
                        value={u.role || "Contributor"} 
                        onOptionSelect={async (_, data) => {
                          await updateUserRole(u.uid, data.optionValue!);
                          setAdminUserList(prev => prev.map(usr => usr.uid === u.uid ? { ...usr, role: data.optionValue } : usr));
                        }}
                        style={{ minWidth: '100px' }}
                      >
                        <Option value="Contributor">Contributor</Option>
                        <Option value="Lead">Lead</Option>
                        <Option value="admin">Admin</Option>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card appearance="outline">
            <CardHeader 
              header={<Subtitle1>Pilot Whitelist</Subtitle1>} 
              description="Enrolled Department Members" 
              action={
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="file" ref={csvInputRef} onChange={handleCSVUpload} accept=".csv" style={{ display: 'none' }} title="Import CSV" />
                  <Button 
                    size="small" 
                    icon={<ArrowUpload24Regular />} 
                    disabled={importingCSV}
                    onClick={() => csvInputRef.current?.click()}
                  >
                    Import CSV
                  </Button>
                </div>
              }
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '12px' }}>
              <Input 
                value={newWhitelistEmail} 
                onChange={(_, data) => setNewWhitelistEmail(data.value)} 
                placeholder="email@company.com" 
                style={{ flexGrow: 1 }} 
              />
              <Button 
                appearance="primary" 
                onClick={async () => {
                  if (!newWhitelistEmail) return;
                  await addToWhitelist(tenantId!, newWhitelistEmail);
                  setAdminWhitelist(prev => [...prev, newWhitelistEmail.toLowerCase()]);
                  setNewWhitelistEmail("");
                }}
              >Add</Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {adminWhitelist.map(email => (
                <div key={email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: '4px' }}>
                  <Body1>{email}</Body1>
                  <Button 
                    size="small" 
                    icon={<Delete24Regular />} 
                    appearance="subtle" 
                    onClick={async () => {
                      await removeFromWhitelist(tenantId!, email);
                      setAdminWhitelist(prev => prev.filter(e => e !== email));
                    }} 
                  />
                </div>
              ))}
            </div>
          </Card>

          {environment === 'staging' && (
            <>
              <Card appearance="outline" style={{ border: `1px solid ${tokens.colorPaletteRedBorder2}` }}>
                <CardHeader 
                  header={<Subtitle1>Data Migration (Beta to Prod)</Subtitle1>} 
                  description="Promote staging workspaces to production"
                />
                <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(() => {
                    const stagingWorkspaces = Array.from(
                      new Set(notes.filter(n => !n.isGlobal).map(n => JSON.stringify({id: n.documentId, name: n.documentName || "Unknown Document"})))
                    ).map(str => JSON.parse(str) as {id: string, name: string});
                    
                    if (stagingWorkspaces.length === 0) {
                      return <Body1 style={{ color: tokens.colorNeutralForeground3 }}>No active staging workspaces to migrate.</Body1>;
                    }

                    return (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '150px', overflowY: 'auto' }}>
                          {stagingWorkspaces.map(ws => (
                            <Checkbox 
                              key={ws.id} 
                              label={ws.name} 
                              checked={selectedMigrateIds.includes(ws.id)}
                              onChange={(_e, data) => {
                                if (data.checked) {
                                  setSelectedMigrateIds(prev => [...prev, ws.id]);
                                } else {
                                  setSelectedMigrateIds(prev => prev.filter(id => id !== ws.id));
                                }
                              }}
                            />
                          ))}
                        </div>
                        <div style={{ marginTop: '12px' }}>
                          <Dialog open={showMigrateDialog} onOpenChange={(_e, data) => setShowMigrateDialog(data.open)}>
                            <DialogTrigger disableButtonEnhancement>
                              <Button appearance="primary" disabled={selectedMigrateIds.length === 0} style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: 'white' }}>
                                Promote Selected
                              </Button>
                            </DialogTrigger>
                            <DialogSurface>
                              <DialogBody>
                                <DialogTitle>Confirm Promotion</DialogTitle>
                                <DialogContent>
                                  <Body1 block style={{ marginBottom: "12px" }}>
                                    You are about to move <b>{selectedMigrateIds.length} workspaces</b> up to the Production environment.
                                  </Body1>
                                  <Body1 block style={{ color: tokens.colorPaletteRedForeground1, fontWeight: "bold" }}>
                                    Warning: These notes will immediately vanish from your Beta view and will permanently exist in Production.
                                  </Body1>
                                </DialogContent>
                                <DialogActions>
                                  <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="secondary" disabled={isMigrating}>Cancel</Button>
                                  </DialogTrigger>
                                  <Button appearance="primary" onClick={handleMigrateWorkspaces} disabled={isMigrating} style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: 'white' }}>
                                    {isMigrating ? "Migrating..." : "Confirm Publish"}
                                  </Button>
                                </DialogActions>
                              </DialogBody>
                            </DialogSurface>
                          </Dialog>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </Card>

              <Card appearance="outline" style={{ border: `1px dashed ${tokens.colorPaletteDarkOrangeBorder2}` }}>
                <CardHeader 
                  header={<Subtitle1>Infrastructure Experiments</Subtitle1>} 
                  description="BETA ONLY: Test experimental features before production"
                />
                <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Body1 block>AI Performance Mode</Body1>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Reduces latency but uses cached models</Caption1>
                    </div>
                    <Switch 
                      checked={betaFeaturesEnabled} 
                      onChange={(_, data) => setBetaFeaturesEnabled(data.checked)} 
                    />
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {selectedValue === "settings" && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '4px 0' }}>
          <Card appearance="outline">
            <CardHeader
              header={<Subtitle1>OneDrive Connection</Subtitle1>}
              description="Links live Excel data and syncs your notes to your vault"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', marginBottom: '10px' }}>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                background: hasSyncToken ? '#e6f4f0' : tokens.colorNeutralBackground5,
                color: hasSyncToken ? '#2f8570' : tokens.colorNeutralForeground3,
              }}>
                {hasSyncToken ? 'Connected ✅' : 'Not connected'}
              </span>
            </div>
            <Body1 style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '12px' }}>
              If Excel notes show a connection or token error, reconnect to refresh your OneDrive
              access. You'll see a Microsoft sign-in prompt.
            </Body1>
            <Button
              appearance="primary"
              style={{ backgroundColor: '#2f8570' }}
              onClick={handleReconnect}
            >
              {hasSyncToken ? 'Reconnect OneDrive' : 'Connect OneDrive'}
            </Button>
            {hasSyncToken && (
              <>
                <Body1 style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '14px', marginBottom: '8px' }}>
                  Re-sync all your notes to the vault as indexable text so Microsoft 365 Copilot
                  can find them. Run this once to migrate older notes.
                </Body1>
                <Button
                  appearance="outline"
                  disabled={resyncing}
                  onClick={handleResyncVault}
                >
                  {resyncing ? 'Re-syncing…' : 'Re-sync all notes to OneDrive'}
                </Button>
              </>
            )}
          </Card>

          <Card appearance="outline">
            <CardHeader header={<Subtitle1>Note Display Mode</Subtitle1>} description="Controls how margin note links appear in the document" />

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '12px' }}>
              {(['visible', 'discreet'] as DisplayMode[]).map(m => (
                <Button
                  key={m}
                  appearance={docDisplaySettings.mode === m ? 'primary' : 'outline'}
                  style={docDisplaySettings.mode === m ? { backgroundColor: '#2f8570' } : {}}
                  onClick={async () => {
                    const newSettings = { ...docDisplaySettings, mode: m };
                    setDocDisplaySettings(newSettings);
                    await saveDocumentDisplaySettings(newSettings);
                  }}
                >
                  {m === 'visible' ? '👁 Visible' : '◆ Discreet'}
                </Button>
              ))}
            </div>

            {/* Mode descriptions */}
            <Body1 style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '12px' }}>
              {docDisplaySettings.mode === 'visible'
                ? 'Note links appear as green, underlined text in the document.'
                : 'Note links match the surrounding document font with a small icon indicator.'}
            </Body1>

            {/* Icon picker — shown when discreet */}
            {docDisplaySettings.mode === 'discreet' && (
              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                  {CLASSIC_ICONS.map(icon => (
                    <Tooltip key={icon.id} content={icon.label} relationship="label">
                      <Button
                        appearance={docDisplaySettings.iconId === icon.id ? 'primary' : 'outline'}
                        size="small"
                        style={{
                          minWidth: '36px', padding: '4px 8px',
                          fontSize: icon.isLogo ? '10px' : '15px',
                          fontWeight: icon.isLogo ? 900 : 400,
                          ...(docDisplaySettings.iconId === icon.id ? { backgroundColor: '#2f8570' } : {}),
                        }}
                        onClick={async () => {
                          const newSettings = { ...docDisplaySettings, iconId: icon.id };
                          setDocDisplaySettings(newSettings);
                          await saveDocumentDisplaySettings(newSettings);
                        }}
                      >
                        {icon.char}
                      </Button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            {/* Apply Now */}
            <div style={{ borderTop: `1px solid ${tokens.colorNeutralStroke1}`, paddingTop: '12px' }}>
              <Body1 style={{ fontWeight: 600, display: 'block', marginBottom: '6px' }}>Apply to this document</Body1>
              <Body1 style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '10px', display: 'block' }}>
                Reformats all existing note links in the open document to match the current mode and icon.
              </Body1>
              <Switch
                label="Include notes with custom overrides"
                checked={includeOverridesInApply}
                onChange={(_, d) => setIncludeOverridesInApply(d.checked)}
              />
              <div style={{ marginTop: '10px' }}>
                <Button
                  appearance="primary"
                  style={{ backgroundColor: '#2f8570' }}
                  disabled={applyingDisplayMode || displayModeNoteCount === 0}
                  onClick={applyDisplayModeToDocument}
                  icon={applyingDisplayMode ? <Spinner size="tiny" /> : undefined}
                >
                  {applyingDisplayMode ? 'Applying...' : `Apply Now (${displayModeNoteCount} note${displayModeNoteCount !== 1 ? 's' : ''})`}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      </div>
      
      <div style={{ padding: '8px 12px', borderTop: `1px solid ${tokens.colorNeutralStroke1}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: tokens.colorNeutralBackground2 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {environment === 'staging' ? `BETA: ${version}` : `MarginPro ${version}`}
        </Caption1>
        {environment === 'staging' && (
          <div style={{ 
            backgroundColor: tokens.colorPaletteDarkOrangeBackground2, 
            color: tokens.colorPaletteDarkOrangeForeground2, 
            fontSize: '9px', 
            fontWeight: 'bold', 
            padding: '2px 6px', 
            borderRadius: '4px',
            textTransform: 'uppercase'
          }}>
            Staging
          </div>
        )}
      </div>
      {/* ── Sign Out Confirmation Dialog ── */}
      {/* ── Settings Panel ─────────────────────────────────────────────────── */}

      <Dialog open={showSignOutDialog} onOpenChange={(_e, data) => setShowSignOutDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Sign Out</DialogTitle>
            <DialogContent>
              <Body1 block style={{ marginBottom: "8px" }}>
                Are you sure you want to sign out?
              </Body1>
              <Body1 block style={{ color: tokens.colorNeutralForeground3 }}>
                You will need to sign back in to access or synchronize your Margin Notes.
              </Body1>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button 
                appearance="primary" 
                onClick={async () => {
                  setShowSignOutDialog(false);
                  try {
                    await logout();
                  } catch (err) {
                    console.error("Logout failed", err);
                  }
                }}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: 'white' }}
              >
                Sign Out
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default MarginMenu;
